import { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { LogOut, Plus, Trash2, Pencil, X, Save } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSiteContent } from "@/context/SiteContentContext";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";

const emptyArtist = {
    name: "",
    slug: "",
    discipline: "DJ",
    disciplines: [],
    category: "music",
    location: "",
    bio: "",
    short_description: "",
    image_url: "",
    image_url_alt: "",
    cover_url: "",
    embeds: [],
    socials: {},
    featured: false,
    home_featured: false,
    works: [],
    latest_work_url: "",
    latest_work_label: "",
    render_preset: "",
    render_asset_url: "",
};
const emptyScene = {
    kind: "editorial",
    title: "",
    subtitle: "",
    hero_image_url: "",
    hero_video_url: "",
    poster_image_url: "",
    short_description: "",
    credit_label: "",
    credit_url: "",
    collaborator_slugs: [],
    tags: [],
    cta_label: "",
    cta_url: "",
    secondary_cta_label: "",
    secondary_cta_url: "",
    embed_url: "",
    embed_label: "",
    related_items: [],
    layout_hint: "auto",
    active: true,
    order: 0,
    scope: "home",
    lang: "both",
    render_preset: "",
    render_asset_url: "",
};
const emptyEvent = {
    title: "",
    date: new Date().toISOString().slice(0, 16),
    venue: "",
    city: "",
    country: "",
    lineup: [],
    ticket_url: "",
    facebook_url: "",
    instagram_url: "",
    image_url: "",
    flyer_url: "",
    media_embeds: [],
    status: "upcoming",
    description: "",
};
const emptyProduct = {
    title: "",
    price: 0,
    currency: "EUR",
    image_url: "",
    image_url_alt: "",
    shopify_url: "",
    description: "",
    rating: 5,
    reviews_count: 0,
    sold_recent: 0,
    featured: false,
    artist_credit: "",
    artist_slug: "",
    product_type: "",
    favorites_count: 0,
    sold_out: false,
    sale_percent: 0,
    render_preset: "",
    render_asset_url: "",
    scheme: "",
};
const emptyCollab = {
    title: "",
    partner: "",
    kind: "brand",
    description: "",
    image_url: "",
    url: "",
    year: String(new Date().getFullYear()),
    featured: false,
};

const emptyAfterDark = {
    kind: "audio",
    title: "",
    title_en: "",
    title_es: "",
    subtitle: "",
    subtitle_en: "",
    subtitle_es: "",
    description: "",
    description_en: "",
    description_es: "",
    embed_url: "",
    media_url: "",
    image_url: "",
    external_url: "",
    artist_slug: "",
    tags: "",
    order: 0,
    active: true,
    featured: false,
    publish_at: "",
    expires_at: "",
};

const Input = ({ label, ...p }) => (
    <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">{label}</span>
        <input {...p} className="mt-1 w-full bg-transparent focus:border-[#5D3FD3] outline-none font-mono text-sm py-2" />
    </label>
);

const Textarea = ({ label, ...p }) => (
    <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">{label}</span>
        <textarea {...p} className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-3" rows={4} />
    </label>
);

// Image field with persistent upload — paste a URL OR upload a file to Emergent
// object storage (served from /api/files/…, survives redeploys, canvas-safe).
const ImageInput = ({ label, value, onChange }) => {
    const [busy, setBusy] = useState(false);
    const doUpload = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = "";
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
            onChange({ target: { value: url } });
            toast.success("image uploaded");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "upload failed");
        }
        setBusy(false);
    };
    return (
        <div className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">{label}</span>
            <div className="mt-1 flex items-center gap-2">
                <input
                    value={value || ""}
                    onChange={onChange}
                    placeholder="https://… or upload →"
                    className="flex-1 w-full bg-transparent focus:border-[#5D3FD3] outline-none font-mono text-sm py-2"
                />
                {value ? <img src={value} alt="" className="w-9 h-9 object-cover border border-[#333] shrink-0" /> : null}
                <label className={`shrink-0 cursor-pointer border border-[#333] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${busy ? "opacity-50" : "hover:bg-[#333] hover:text-white"}`} data-testid="image-upload-btn">
                    {busy ? "uploading…" : "upload"}
                    <input type="file" accept="image/*" className="hidden" onChange={doUpload} disabled={busy} />
                </label>
            </div>
        </div>
    );
};

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const [tab, setTab] = useState("artists");
    const [artists, setArtists] = useState([]);
    const [events, setEvents] = useState([]);
    const [products, setProducts] = useState([]);
    const [collabs, setCollabs] = useState([]);
    const [scenes, setScenes] = useState([]);
    const [afterDark, setAfterDark] = useState([]);
    const [editing, setEditing] = useState(null); // {type, data}

    const load = async () => {
        const [a, e, p, c, s, ad] = await Promise.all([
            api.get("/artists"),
            api.get("/events"),
            api.get("/products"),
            api.get("/collaborations"),
            api.get("/scene", { params: { active_only: false } }),
            api.get("/admin/after-dark").catch(() => ({ data: [] })),
        ]);
        setArtists(a.data);
        setEvents(e.data);
        setProducts(p.data);
        setCollabs(c.data);
        setScenes(Array.isArray(s.data) ? s.data : []);
        setAfterDark(Array.isArray(ad.data) ? ad.data : []);
    };
    useEffect(() => {
        if (user && user.email) load();
    }, [user]);

    if (user === null) return <div className="p-16 font-mono text-xs uppercase tracking-[0.2em]">loading…</div>;
    if (!user || !user.email) return <Navigate to="/admin" replace />;

    const openNew = () => {
        if (tab === "artists") setEditing({ type: "artist", data: { ...emptyArtist } });
        if (tab === "events") setEditing({ type: "event", data: { ...emptyEvent } });
        if (tab === "products") setEditing({ type: "product", data: { ...emptyProduct } });
        if (tab === "collabs") setEditing({ type: "collab", data: { ...emptyCollab } });
        if (tab === "scene") setEditing({ type: "scene", data: { ...emptyScene } });
        if (tab === "after-dark") setEditing({ type: "after-dark", data: { ...emptyAfterDark } });
    };
    const openEdit = (type, item) => setEditing({ type, data: { ...item } });
    const close = () => setEditing(null);

    const save = async () => {
        if (!editing) return;
        const { type, data } = editing;
        const path =
            type === "artist"
                ? "/artists"
                : type === "event"
                ? "/events"
                : type === "product"
                ? "/products"
                : type === "scene"
                ? "/scene"
                : type === "after-dark"
                ? "/after-dark"
                : "/collaborations";
        try {
            const body = { ...data };
            if (type === "artist") {
                if (typeof body.embeds === "string") body.embeds = body.embeds.split("\n").map((s) => s.trim()).filter(Boolean);
                if (typeof body.works === "string") body.works = body.works.split("\n").map((s) => s.trim()).filter(Boolean);
                if (typeof body.disciplines === "string") body.disciplines = body.disciplines.split(",").map((s) => s.trim()).filter(Boolean);
                if (!Array.isArray(body.disciplines)) body.disciplines = [];
            }
            if (type === "scene") {
                if (typeof body.tags === "string") body.tags = body.tags.split(",").map((s) => s.trim()).filter(Boolean);
                if (!Array.isArray(body.tags)) body.tags = [];
                if (typeof body.collaborator_slugs === "string") body.collaborator_slugs = body.collaborator_slugs.split(",").map((s) => s.trim()).filter(Boolean);
                if (!Array.isArray(body.collaborator_slugs)) body.collaborator_slugs = [];
                if (!Array.isArray(body.related_items)) body.related_items = [];
                body.related_items = body.related_items
                    .map((it) => ({
                        title: (it.title || "").trim(),
                        subtitle: (it.subtitle || "").trim(),
                        image_url: (it.image_url || "").trim(),
                        url: (it.url || "").trim(),
                        kind: (it.kind || "").trim(),
                    }))
                    .filter((it) => it.title || it.image_url);
                body.order = parseInt(body.order, 10) || 0;
            }
            if (type === "event") {
                if (typeof body.lineup === "string") body.lineup = body.lineup.split(",").map((s) => s.trim()).filter(Boolean);
                // Convert datetime-local to ISO
                if (body.date && !body.date.endsWith("Z") && body.date.length <= 16) body.date = new Date(body.date).toISOString();
            }
            if (type === "product") {
                // Coerce all numeric fields — inputs come back as strings, and any
                // int field with a decimal will trip Pydantic ("valid integer" error).
                const toInt = (v) => {
                    const n = parseInt(v, 10);
                    return Number.isFinite(n) ? n : 0;
                };
                const toFloat = (v) => {
                    const n = parseFloat(v);
                    return Number.isFinite(n) ? n : 0;
                };
                body.price = toFloat(body.price);
                body.rating = toFloat(body.rating);
                body.reviews_count = toInt(body.reviews_count);
                body.sold_recent = toInt(body.sold_recent);
                body.favorites_count = toInt(body.favorites_count);
                body.sale_percent = toInt(body.sale_percent);
                body.quantity = toInt(body.quantity);
            }
            if (type === "after-dark") {
                if (typeof body.tags === "string") body.tags = body.tags.split(",").map((s) => s.trim()).filter(Boolean);
                if (!Array.isArray(body.tags)) body.tags = [];
                body.order = parseInt(body.order, 10) || 0;
                body.active = !!body.active;
                body.featured = !!body.featured;
            }
            if (data.id) await api.put(`${path}/${data.id}`, body);
            else await api.post(path, body);
            toast.success("saved");
            close();
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "save failed");
        }
    };

    const remove = async (type, id) => {
        if (!window.confirm("delete permanently?")) return;
        const path =
            type === "artist"
                ? "/artists"
                : type === "event"
                ? "/events"
                : type === "product"
                ? "/products"
                : type === "scene"
                ? "/scene"
                : type === "after-dark"
                ? "/after-dark"
                : "/collaborations";
        try {
            await api.delete(`${path}/${id}`);
            toast.success("deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "delete failed");
        }
    };

    return (
        <div data-testid="admin-dashboard" className="min-h-[80vh]">
            <Toaster richColors position="top-right" />
            <div className="px-4 md:px-8 py-6 flex items-center justify-between gap-4">
                <div>
                    <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#555]">control room</div>
                    <div className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tighter">
                        hi, {user.name || user.email}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <Link to="/" className="font-mono text-xs uppercase tracking-[0.2em] link-underline">
                        view site →
                    </Link>
                    <button
                        onClick={logout}
                        data-testid="admin-logout"
                        className="inline-flex items-center gap-2 border border-[#333] px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#333] hover:text-[#FFFFFF] transition-colors"
                    >
                        <LogOut size={12} /> logout
                    </button>
                </div>
            </div>

            <div className="px-4 md:px-8 py-3 flex flex-wrap gap-2 items-center">
                {["artists", "events", "products", "etsy", "collabs", "scene", "after-dark", "site"].map((t) => (
                    <button
                        key={t}
                        onClick={() => setTab(t)}
                        data-testid={`admin-tab-${t}`}
                        className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-[#333] transition-colors ${
                            tab === t ? "bg-[#333] text-[#FFFFFF]" : "hover:bg-[#333] hover:text-[#FFFFFF]"
                        }`}
                    >
                        {t}
                    </button>
                ))}
                <div className="ml-auto">
                    {tab !== "site" && (
                        <button
                            onClick={openNew}
                            data-testid="admin-new"
                            className="inline-flex items-center gap-2 bg-[#333] text-[#FFFFFF] hover:bg-[#5D3FD3] hover:text-[#333] transition-colors px-4 py-2 font-mono text-xs uppercase tracking-[0.2em]"
                        >
                            <Plus size={12} /> new
                        </button>
                    )}
                </div>
            </div>

            <div className="px-4 md:px-8 py-8">
                {tab === "artists" && (
                    <ul className="border-t border-[#EEE]">
                        {artists.map((a) => (
                            <li key={a.id} data-testid={`admin-artist-${a.id}`} className="py-4 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-4 min-w-0">
                                    <div className="w-14 h-14 border border-[#333] overflow-hidden shrink-0 bg-[#333]">
                                        {a.image_url && <img src={a.image_url} alt="" className="w-full h-full object-cover grayscale" />}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-heading font-bold uppercase truncate">{a.name}</div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                                            {a.discipline} · {a.slug} {a.featured && "· ★ featured"}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button onClick={() => openEdit("artist", a)} className="border border-[#333] p-2 hover:bg-[#333] hover:text-[#FFFFFF]" data-testid={`edit-artist-${a.id}`}>
                                        <Pencil size={12} />
                                    </button>
                                    <button onClick={() => remove("artist", a.id)} className="border border-[#333] p-2 hover:bg-[#5D3FD3] hover:text-[#333]" data-testid={`delete-artist-${a.id}`}>
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                {tab === "events" && (
                    <ul className="border-t border-[#EEE]">
                        {events.map((e) => (
                            <li key={e.id} data-testid={`admin-event-${e.id}`} className="py-4 flex items-center justify-between gap-4">
                                <div className="min-w-0">
                                    <div className="font-heading font-bold uppercase truncate">{e.title}</div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                                        {new Date(e.date).toLocaleString()} · {e.venue}, {e.city} · {e.status}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button onClick={() => openEdit("event", e)} className="border border-[#333] p-2 hover:bg-[#333] hover:text-[#FFFFFF]" data-testid={`edit-event-${e.id}`}>
                                        <Pencil size={12} />
                                    </button>
                                    <button onClick={() => remove("event", e.id)} className="border border-[#333] p-2 hover:bg-[#5D3FD3] hover:text-[#333]" data-testid={`delete-event-${e.id}`}>
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                {tab === "products" && (
                    <ul className="border-t border-[#EEE]">
                        {products.map((p) => (
                            <li key={p.id} data-testid={`admin-product-${p.id}`} className="py-4 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-4 min-w-0">
                                    <div className="w-14 h-14 border border-[#333] overflow-hidden shrink-0">
                                        {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-heading font-bold uppercase truncate">{p.title}</div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                                            {p.currency} {p.price} · ★ {p.rating} ({p.reviews_count})
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button onClick={() => openEdit("product", p)} className="border border-[#333] p-2 hover:bg-[#333] hover:text-[#FFFFFF]" data-testid={`edit-product-${p.id}`}>
                                        <Pencil size={12} />
                                    </button>
                                    <button onClick={() => remove("product", p.id)} className="border border-[#333] p-2 hover:bg-[#5D3FD3] hover:text-[#333]" data-testid={`delete-product-${p.id}`}>
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                {tab === "collabs" && (
                    <ul className="border-t border-[#EEE]">
                        {collabs.map((c) => (
                            <li key={c.id} data-testid={`admin-collab-${c.id}`} className="py-4 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-4 min-w-0">
                                    <div className="w-14 h-14 border border-[#333] overflow-hidden shrink-0">
                                        {c.image_url && <img src={c.image_url} alt="" className="w-full h-full object-cover grayscale" />}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-heading font-bold uppercase truncate">{c.title}</div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                                            {c.kind} · w/ {c.partner} {c.year && `· ${c.year}`} {c.featured && "· ★ featured"}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button onClick={() => openEdit("collab", c)} className="border border-[#333] p-2 hover:bg-[#333] hover:text-[#FFFFFF]" data-testid={`edit-collab-${c.id}`}>
                                        <Pencil size={12} />
                                    </button>
                                    <button onClick={() => remove("collab", c.id)} className="border border-[#333] p-2 hover:bg-[#5D3FD3] hover:text-[#333]" data-testid={`delete-collab-${c.id}`}>
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}


                {tab === "etsy" && <EtsyImporter onImported={load} />}

                {tab === "scene" && (
                    <ul className="border-t border-[#EEE]">
                        {scenes.length === 0 && (
                            <li className="py-8 font-mono text-xs uppercase tracking-[0.3em] text-[#777]">
                                no scene features yet — click <b>NEW</b> to create the first curated block.
                            </li>
                        )}
                        {scenes.map((s) => (
                            <li key={s.id} data-testid={`admin-scene-${s.id}`} className="py-4 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-4 min-w-0">
                                    <div className="w-14 h-14 border border-[#333] overflow-hidden shrink-0 bg-[#111]">
                                        {s.hero_image_url && <img src={s.hero_image_url} alt="" className="w-full h-full object-cover" />}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-heading font-bold uppercase truncate">{s.title || "(untitled)"}</div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                                            {s.kind} · scope: {s.scope} · order {s.order}
                                            {s.active === false && " · hidden"}
                                            {s.migrated_from === "media_player" && " · migrated"}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button onClick={() => openEdit("scene", s)} className="border border-[#333] p-2 hover:bg-[#333] hover:text-[#FFFFFF]" data-testid={`edit-scene-${s.id}`}>
                                        <Pencil size={12} />
                                    </button>
                                    <button onClick={() => remove("scene", s.id)} className="border border-[#333] p-2 hover:bg-[#5D3FD3] hover:text-[#333]" data-testid={`delete-scene-${s.id}`}>
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                {tab === "after-dark" && (
                    <div>
                        <div className="mb-6 p-4 bg-[#0B0B0C] text-white">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F0C56C] mb-2">
                                after dark · qr endpoint
                            </div>
                            <div className="font-mono text-xs text-white/70">
                                url pública fija: <b>/after-dark</b> — el QR impreso siempre apunta acá, actualizás el contenido cuando quieras.
                            </div>
                        </div>
                        <ul className="border-t border-[#EEE]">
                            {afterDark.length === 0 && (
                                <li className="py-8 font-mono text-xs uppercase tracking-[0.3em] text-[#777]">
                                    no items yet — click <b>NEW</b> to drop something in the hidden channel.
                                </li>
                            )}
                            {afterDark.map((s) => (
                                <li key={s.id} data-testid={`admin-after-dark-${s.id}`} className="py-4 flex items-center justify-between gap-4">
                                    <div className="flex items-center gap-4 min-w-0">
                                        <div className="w-14 h-14 overflow-hidden shrink-0 bg-[#111]">
                                            {s.image_url && <img src={s.image_url} alt="" className="w-full h-full object-cover" />}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-heading font-bold uppercase truncate">
                                                {s.title || "(untitled)"} {s.featured && <span className="ml-2 text-[10px] font-mono text-[#F0C56C]">FEATURED</span>}
                                            </div>
                                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                                                {s.kind} · order {s.order}
                                                {s.active === false && " · hidden"}
                                                {s.publish_at && ` · publish ${s.publish_at.slice(0,10)}`}
                                                {s.expires_at && ` · exp ${s.expires_at.slice(0,10)}`}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <button onClick={() => openEdit("after-dark", s)} className="border border-[#333] p-2 hover:bg-[#333] hover:text-[#FFFFFF]" data-testid={`edit-after-dark-${s.id}`}>
                                            <Pencil size={12} />
                                        </button>
                                        <button onClick={() => remove("after-dark", s.id)} className="border border-[#333] p-2 hover:bg-[#5D3FD3] hover:text-[#333]" data-testid={`delete-after-dark-${s.id}`}>
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {tab === "site" && <SiteEditor artistsList={artists} />}
            </div>

            {editing && (
                <div className="fixed inset-0 z-[70] bg-[#333]/70 flex items-end md:items-center md:justify-center" onClick={close}>
                    <div
                        className="bg-[#FFFFFF] border border-[#333] w-full md:max-w-2xl max-h-[92vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="admin-modal"
                    >
                        <div className="flex items-center justify-between p-4">
                            <div className="font-heading font-bold uppercase">
                                {editing.data.id ? "edit" : "new"} {editing.type}
                            </div>
                            <button onClick={close} className="p-2 border border-[#333]" data-testid="admin-modal-close">
                                <X size={12} />
                            </button>
                        </div>
                        <div className="p-4 md:p-6 space-y-4">
                            {editing.type === "artist" && (
                                <>
                                    <Input label="name" value={editing.data.name} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, name: e.target.value } })} data-testid="field-name" />
                                    <Input label="slug (optional)" value={editing.data.slug} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, slug: e.target.value } })} />
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">category — broad editorial bucket (drives the /artists filter tabs)</span>
                                        <select
                                            value={editing.data.category || "music"}
                                            onChange={(e) => setEditing({ ...editing, data: { ...editing.data, category: e.target.value } })}
                                            data-testid="field-category"
                                            className="mt-1 w-full bg-transparent border border-[#333] font-mono text-sm p-2 focus:border-[#5D3FD3] outline-none"
                                        >
                                            <option value="featured">Featured</option>
                                            <option value="music">Music</option>
                                            <option value="visual_arts">Visual arts</option>
                                            <option value="objects">Objects</option>
                                            <option value="apparel">Apparel</option>
                                            <option value="collective">Collective</option>
                                        </select>
                                    </label>
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">role — appears in a different section on /artists</span>
                                        <select
                                            value={editing.data.role || "artist"}
                                            onChange={(e) => setEditing({ ...editing, data: { ...editing.data, role: e.target.value } })}
                                            data-testid="field-role"
                                            className="mt-1 w-full bg-transparent border border-[#333] font-mono text-sm p-2 focus:border-[#5D3FD3] outline-none"
                                        >
                                            <option value="artist">Artist (in the collective)</option>
                                            <option value="collaborator">Collaborator (frequent guest / partner)</option>
                                        </select>
                                    </label>
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">discipline (primary — type any or pick a suggestion)</span>
                                        <input
                                            list="discipline-suggestions"
                                            value={editing.data.discipline}
                                            onChange={(e) => setEditing({ ...editing, data: { ...editing.data, discipline: e.target.value } })}
                                            data-testid="field-discipline"
                                            placeholder="e.g. DJ, Vocalist, Multidisciplinary…"
                                            className="mt-1 w-full bg-transparent focus:border-[#5D3FD3] outline-none font-mono text-sm py-2"
                                        />
                                        <datalist id="discipline-suggestions">
                                            {["DJ", "Rapper", "Producer", "Visual Artist", "Creative Programmer", "Multidisciplinary", "Vocalist", "Photographer", "Designer", "Filmmaker"].map((d) => (
                                                <option key={d} value={d} />
                                            ))}
                                        </datalist>
                                    </label>
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">extra tags (comma-separated — an artist can wear many hats)</span>
                                        <input
                                            value={Array.isArray(editing.data.disciplines) ? editing.data.disciplines.join(", ") : (editing.data.disciplines || "")}
                                            onChange={(e) => setEditing({ ...editing, data: { ...editing.data, disciplines: e.target.value } })}
                                            data-testid="field-disciplines"
                                            placeholder="e.g. Producer, Visual Artist, Filmmaker"
                                            className="mt-1 w-full bg-transparent focus:border-[#5D3FD3] outline-none font-mono text-sm py-2"
                                        />
                                        <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#999] mt-1 block">duplicates are ignored (case-insensitive)</span>
                                    </label>
                                    <Input label="location" value={editing.data.location} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, location: e.target.value } })} />
                                    <Textarea label="short description (1-2 lines shown on the /artists card — fallback: first sentence of bio)" rows={2} value={editing.data.short_description || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, short_description: e.target.value } })} />
                                    <Textarea label="bio" value={editing.data.bio} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, bio: e.target.value } })} />
                                    <ImageInput label="image url" value={editing.data.image_url} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, image_url: e.target.value } })} />
                                    <ImageInput label="image url alt (hover swap)" value={editing.data.image_url_alt || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, image_url_alt: e.target.value } })} />
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <Input label="latest work — label (override)" value={editing.data.latest_work_label || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, latest_work_label: e.target.value } })} placeholder="e.g. New release · Espejo Roto" />
                                        <Input label="latest work — url (override)" value={editing.data.latest_work_url || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, latest_work_url: e.target.value } })} placeholder="/artists/... or https://…" />
                                    </div>
                                    <RenderPresetPicker
                                        preset={editing.data.render_preset}
                                        assetUrl={editing.data.render_asset_url}
                                        onChange={(patch) => setEditing({ ...editing, data: { ...editing.data, ...patch } })}
                                    />
                                    <SchemePicker
                                        scheme={editing.data.scheme}
                                        onChange={(v) => setEditing({ ...editing, data: { ...editing.data, scheme: v } })}
                                        label="color scheme for this artist's DETAIL PAGE (the &quot;room&quot; visitors enter)"
                                        testId="artist-scheme"
                                    />
                                    <Textarea
                                        label="embeds (one url per line — soundcloud/spotify/youtube iframe src)"
                                        value={Array.isArray(editing.data.embeds) ? editing.data.embeds.join("\n") : editing.data.embeds}
                                        onChange={(e) => setEditing({ ...editing, data: { ...editing.data, embeds: e.target.value } })}
                                    />
                                    <Textarea
                                        label="works (one image url per line)"
                                        value={Array.isArray(editing.data.works) ? editing.data.works.join("\n") : editing.data.works}
                                        onChange={(e) => setEditing({ ...editing, data: { ...editing.data, works: e.target.value } })}
                                    />
                                    <Input label="instagram url" value={editing.data.socials?.instagram || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, socials: { ...editing.data.socials, instagram: e.target.value } } })} />
                                    <Input label="soundcloud url" value={editing.data.socials?.soundcloud || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, socials: { ...editing.data.socials, soundcloud: e.target.value } } })} />
                                    <Input label="spotify url" value={editing.data.socials?.spotify || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, socials: { ...editing.data.socials, spotify: e.target.value } } })} />
                                    <Input label="youtube url" value={editing.data.socials?.youtube || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, socials: { ...editing.data.socials, youtube: e.target.value } } })} />
                                    <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                                        <input type="checkbox" checked={!!editing.data.featured} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, featured: e.target.checked } })} />
                                        featured (show in roster on home)
                                    </label>
                                    <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                                        <input type="checkbox" checked={!!editing.data.home_featured} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, home_featured: e.target.checked } })} />
                                        ★ home featured artist (the big card)
                                    </label>
                                </>
                            )}

                            {editing.type === "event" && (
                                <>
                                    <Input label="title" value={editing.data.title} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, title: e.target.value } })} />
                                    <Input
                                        label="date/time (in your local timezone — no conversion)"
                                        type="datetime-local"
                                        value={typeof editing.data.date === "string" ? editing.data.date.slice(0, 16) : ""}
                                        onChange={(e) => setEditing({ ...editing, data: { ...editing.data, date: e.target.value } })}
                                    />
                                    <Input label="venue" value={editing.data.venue} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, venue: e.target.value } })} />
                                    <Input label="venue url (facebook / instagram / website)" value={editing.data.venue_url || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, venue_url: e.target.value } })} />
                                    <Input label="city" value={editing.data.city} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, city: e.target.value } })} />
                                    <Input label="country" value={editing.data.country} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, country: e.target.value } })} />
                                    <Input
                                        label="lineup (comma separated)"
                                        value={Array.isArray(editing.data.lineup) ? editing.data.lineup.join(", ") : editing.data.lineup}
                                        onChange={(e) => setEditing({ ...editing, data: { ...editing.data, lineup: e.target.value } })}
                                    />
                                    <Input label="ticket url" value={editing.data.ticket_url} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, ticket_url: e.target.value } })} />
                                    <Input label="facebook url (fallback if no ticket)" value={editing.data.facebook_url || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, facebook_url: e.target.value } })} />
                                    <Input label="instagram url (fallback if no ticket)" value={editing.data.instagram_url || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, instagram_url: e.target.value } })} />
                                    <ImageInput label="image url" value={editing.data.image_url || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, image_url: e.target.value } })} />
                                    <ImageInput label="flyer url (hover preview on events list)" value={editing.data.flyer_url || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, flyer_url: e.target.value } })} />
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">media embeds (one url per line — instagram, facebook, youtube)</span>
                                        <textarea
                                            data-testid="event-media-embeds"
                                            rows={4}
                                            value={(editing.data.media_embeds || []).join("\n")}
                                            onChange={(e) => setEditing({ ...editing, data: { ...editing.data, media_embeds: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) } })}
                                            placeholder="https://www.instagram.com/reel/ABC123/&#10;https://www.facebook.com/watch/?v=1234567&#10;https://www.youtube.com/watch?v=xxxx"
                                            className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-xs p-2"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">status</span>
                                        <select
                                            value={editing.data.status}
                                            onChange={(e) => setEditing({ ...editing, data: { ...editing.data, status: e.target.value } })}
                                            className="mt-1 w-full bg-transparent font-mono text-sm py-2"
                                        >
                                            <option value="upcoming">upcoming</option>
                                            <option value="past">past</option>
                                        </select>
                                    </label>
                                    <Textarea label="description" value={editing.data.description} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, description: e.target.value } })} />
                                </>
                            )}

                            {editing.type === "product" && (
                                <>
                                    <Input label="title" value={editing.data.title} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, title: e.target.value } })} />
                                    <Input label="price" type="number" step="0.01" value={editing.data.price} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, price: e.target.value } })} />
                                    <Input label="currency" value={editing.data.currency} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, currency: e.target.value } })} />
                                    <ImageInput label="image url" value={editing.data.image_url} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, image_url: e.target.value } })} />
                                    <ImageInput label="image url alt (hover swap)" value={editing.data.image_url_alt || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, image_url_alt: e.target.value } })} />
                                    <Input label="shopify product url" value={editing.data.shopify_url} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, shopify_url: e.target.value } })} />
                                    <Input label="artist credit (e.g. art by ESPEJO)" value={editing.data.artist_credit || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, artist_credit: e.target.value } })} />
                                    <Input label="artist slug (link to /artists/&lt;slug&gt;)" value={editing.data.artist_slug || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, artist_slug: e.target.value } })} />
                                    <div className="grid grid-cols-2 gap-4">
                                        <Input label="product type (e.g. t-shirt, hoodie, print, vinyl)" value={editing.data.product_type || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, product_type: e.target.value } })} />
                                        <Input label="tags / categories — comma separated (e.g. t-shirts, new, itzkatu xyz)" value={(editing.data.tags || []).join(", ")} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })} />
                                        <Input label="favorites count" type="number" value={editing.data.favorites_count || 0} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, favorites_count: e.target.value } })} />
                                    </div>
                                    <Textarea label="description" value={editing.data.description} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, description: e.target.value } })} />
                                    <div className="grid grid-cols-3 gap-4">
                                        <Input label="rating" type="number" step="0.1" value={editing.data.rating} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, rating: e.target.value } })} />
                                        <Input label="reviews count" type="number" value={editing.data.reviews_count} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, reviews_count: e.target.value } })} />
                                        <Input label="sold recent" type="number" value={editing.data.sold_recent} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, sold_recent: e.target.value } })} />
                                    </div>
                                    <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                                        <input type="checkbox" checked={!!editing.data.featured} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, featured: e.target.checked } })} />
                                        featured
                                    </label>
                                    <div className="grid grid-cols-2 gap-4 items-end">
                                        <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                                            <input type="checkbox" data-testid="product-sold-out" checked={!!editing.data.sold_out} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, sold_out: e.target.checked } })} />
                                            sold out (shows &quot;sold out&quot; badge + disables buy CTA)
                                        </label>
                                        <Input label="sale % (0 = no sale; e.g. 15 = -15% badge + strikes price)" type="number" min="0" max="99" data-testid="product-sale-percent" value={editing.data.sale_percent || 0} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, sale_percent: parseInt(e.target.value) || 0 } })} />
                                    </div>
                                    <RenderPresetPicker
                                        preset={editing.data.render_preset}
                                        assetUrl={editing.data.render_asset_url}
                                        onChange={(patch) => setEditing({ ...editing, data: { ...editing.data, ...patch } })}
                                    />
                                    <SchemePicker
                                        scheme={editing.data.scheme}
                                        onChange={(v) => setEditing({ ...editing, data: { ...editing.data, scheme: v } })}
                                        label="color scheme for this product's DETAIL PAGE (its own room)"
                                        testId="product-scheme"
                                    />
                                </>
                            )}

                            {editing.type === "collab" && (
                                <>
                                    <Input label="title" value={editing.data.title} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, title: e.target.value } })} />
                                    <Input label="partner" value={editing.data.partner} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, partner: e.target.value } })} />
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">kind</span>
                                        <select
                                            value={editing.data.kind}
                                            onChange={(e) => setEditing({ ...editing, data: { ...editing.data, kind: e.target.value } })}
                                            className="mt-1 w-full bg-transparent font-mono text-sm py-2"
                                        >
                                            {["brand", "venue", "festival", "label", "artist"].map((k) => (
                                                <option key={k}>{k}</option>
                                            ))}
                                        </select>
                                    </label>
                                    <Textarea label="description" value={editing.data.description} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, description: e.target.value } })} />
                                    <ImageInput label="image url" value={editing.data.image_url} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, image_url: e.target.value } })} />
                                    <Input label="external url" value={editing.data.url} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, url: e.target.value } })} />
                                    <Input label="year" value={editing.data.year} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, year: e.target.value } })} />
                                    <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                                        <input type="checkbox" checked={!!editing.data.featured} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, featured: e.target.checked } })} />
                                        featured on homepage
                                    </label>
                                </>
                            )}

                            {editing.type === "scene" && (
                                <SceneEditor
                                    data={editing.data}
                                    setData={(patch) => setEditing({ ...editing, data: { ...editing.data, ...patch } })}
                                    artists={artists}
                                />
                            )}

                            {editing.type === "after-dark" && (
                                <AfterDarkEditor
                                    data={editing.data}
                                    setData={(patch) => setEditing({ ...editing, data: { ...editing.data, ...patch } })}
                                    artists={artists}
                                />
                            )}
                        </div>
                        <div className="p-4 flex justify-end gap-3">
                            <button onClick={close} className="border border-[#333] px-4 py-2 font-mono text-xs uppercase tracking-[0.2em]">
                                cancel
                            </button>
                            <button
                                onClick={save}
                                data-testid="admin-save"
                                className="bg-[#333] text-[#FFFFFF] hover:bg-[#5D3FD3] hover:text-[#333] transition-colors px-4 py-2 font-mono text-xs uppercase tracking-[0.2em]"
                            >
                                save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------- SITE CONTENT EDITOR ----------
// Organized by page / section so admins can find things fast.
const CONTENT_GROUPS = [
    {
        title: "HOME · music widget (Spotify / SoundCloud / YouTube embed URL)",
        keys: ["homepage_music_url", "homepage_music_title", "homepage_music_caption"],
    },
    {
        title: "HOME · hero",
        keys: ["hero_kicker", "hero_tag", "hero_carousel_cta"],
    },
    {
        title: "HOME · about section",
        keys: ["about_kicker", "about_title", "about_body"],
    },
    {
        title: "HOME · section kickers (small labels above each block)",
        keys: ["shop_kicker", "featured_artist_kicker", "section_roster", "section_agenda", "section_manifesto"],
    },
    {
        title: "HOME · manifesto block",
        keys: ["manifesto_h", "manifesto_body_1", "manifesto_body_2", "manifesto_cta"],
    },
    {
        title: "HOME · shared buttons / CTAs",
        keys: ["hero_cta_roster", "shop_all", "full_agenda", "more_from_artist", "upcoming_h", "header_shop_cta"],
    },
    {
        title: "SHOP page · texts",
        keys: ["shop_intro", "visit_full_store", "buy_shopify", "credit_prefix"],
    },
    {
        title: "EVENTS page · buttons",
        keys: ["tickets"],
    },
    {
        title: "CONTACT page · texts",
        keys: ["contact_h", "contact_intro"],
    },
    {
        title: "STUDIO / ABOUT page · teaser",
        keys: ["studio_teaser_h", "studio_teaser_body", "studio_cta"],
    },
    {
        title: "STATEMENT · shared with all members (markdown supported)",
        keys: ["member_statement"],
    },
    {
        title: "ARTIST detail page",
        keys: ["listen_watch"],
    },
];
const FOOTER_KEYS = [
    ["footer_instagram_url", "instagram url"],
    ["footer_soundcloud_url", "soundcloud url"],
    ["footer_email", "contact email (mailto:… or plain)"],
];
const NAV_KEYS = ["index", "artists", "events", "shop", "studio", "contact", "lab"];
const SECTION_KEYS = [
    ["section_show_announce", "announcement bar (top of every page)", "announce"],
    ["section_show_kicker_hero", "hero (top of home)", "hero"],
    ["section_show_categories", "categories grid (image tiles)", "categories"],
    ["section_show_shop", "shop grid", "shop"],
    ["section_show_featured_artist", "featured artist (en escena)", "featured_artist"],
    ["section_show_artists", "artists (flowing list)", "artists"],
    ["section_show_roster_grid", "artist roster grid (like /artists)", "roster_grid"],
    ["section_show_events", "events (upcoming list)", "events"],
    ["section_show_editorial", "editorial banner (title + body + cta)", "editorial"],
    ["section_show_about", "about the collective", "about"],
    ["section_show_manifesto", "manifesto", "manifesto"],
    ["section_show_media_player", "the scene (tiles link out, no player)", "media_player"],
    ["section_show_radio", "itzkatu radio (scene copy WITH players)", "radio"],
    ["section_show_join", "join the collective (invite form)", "join"],
    ["section_show_subscribe", "newsletter subscribe (email only)", "subscribe"],
];
// per-section kicker content-key so the row can edit the small purple label
const KICKER_KEY = {
    shop: "shop_kicker",
    featured_artist: "featured_artist_kicker",
    artists: "section_roster",
    events: "section_agenda",
    about: "about_kicker",
    manifesto: "section_manifesto",
    media_player: "media_player_kicker",
    radio: "radio_kicker",
    roster_grid: "roster_grid_kicker",
    editorial: "editorial_kicker",
    join: "join_kicker",
    subscribe: "subscribe_kicker",
    categories: "cats_kicker",
    announce: "announce_text",
};


// -----------------------------------------------------------------------------
// Etsy Importer — top-level tab.
// Fetches the shop's active listings via /api/etsy/listings and lets admin pick
// which ones to import. Also shows a "sync" button per already-imported product
// (via product edit view; here we just import).
// -----------------------------------------------------------------------------
function EtsyImporter({ onImported }) {
    const [listings, setListings] = useState([]);
    const [selected, setSelected] = useState({}); // { listing_id: true }
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setStatus("");
        try {
            const { data } = await api.get("/etsy/listings");
            setListings(data.results || []);
        } catch (e) {
            setStatus(`error: ${e.response?.data?.detail || e.message}`);
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const importSelected = async () => {
        const ids = Object.keys(selected).filter((k) => selected[k]);
        if (ids.length === 0) return;
        setStatus("importing…");
        try {
            const { data } = await api.post("/etsy/import", { listing_ids: ids });
            setStatus(`✓ imported ${data.created} · skipped ${data.skipped}`);
            setSelected({});
            await load();
            if (onImported) onImported();
        } catch (e) {
            setStatus(`error: ${e.response?.data?.detail || e.message}`);
        }
    };

    const selectedCount = Object.values(selected).filter(Boolean).length;

    return (
        <div className="space-y-4" data-testid="etsy-importer">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-heading font-black text-2xl uppercase tracking-tight">import from etsy</h2>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#555] mt-1">
                        listings activos de tu tienda etsy. checkea los que quieres traer a itzkatu. import es solo inicial — después puedes editar sin que se sobrescriba.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button onClick={load} className="border border-[#333] px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#333] hover:text-white" data-testid="etsy-refresh">
                        {loading ? "…" : "refresh"}
                    </button>
                    <button onClick={importSelected} disabled={selectedCount === 0} className="bg-[#5D3FD3] text-white px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] hover:bg-black disabled:opacity-40" data-testid="etsy-import-btn">
                        import selected ({selectedCount})
                    </button>
                </div>
            </div>

            {status && (
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5D3FD3]" data-testid="etsy-status">{status}</div>
            )}

            {loading && listings.length === 0 && (
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">loading listings from etsy…</div>
            )}

            {!loading && listings.length === 0 && (
                <div className="border border-dashed border-[#333] p-8 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                    no active listings in your etsy shop yet. publica un producto en etsy y aprieta refresh.<br/>
                    (a veces etsy tarda 5-30 min en propagar listings nuevos a la api.)
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="etsy-listings-grid">
                {listings.map((l) => (
                    <label key={l.etsy_listing_id} className={`flex flex-col border p-3 gap-2 cursor-pointer ${l.already_imported ? "opacity-40 border-[#DDD]" : "border-[#333] hover:border-[#5D3FD3]"}`}>
                        <div className="flex items-start gap-2">
                            <input
                                type="checkbox"
                                disabled={l.already_imported}
                                checked={!!selected[l.etsy_listing_id]}
                                onChange={(e) => setSelected({ ...selected, [l.etsy_listing_id]: e.target.checked })}
                                data-testid={`etsy-listing-${l.etsy_listing_id}`}
                                className="mt-1"
                            />
                            {l.image_url && <img src={l.image_url} alt={l.title} className="w-24 h-24 object-cover" referrerPolicy="no-referrer" />}
                            <div className="flex-1 min-w-0">
                                <div className="font-heading font-medium text-sm leading-tight line-clamp-2">{l.title}</div>
                                <div className="font-mono text-[10px] text-[#555] mt-1">${l.price} {l.currency}</div>
                                <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#777] mt-1">qty: {l.quantity} · tags: {(l.tags || []).slice(0,3).join(", ")}</div>
                                {l.already_imported && <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#5D3FD3] mt-1">already imported</div>}
                            </div>
                        </div>
                        <a href={l.etsy_url} target="_blank" rel="noreferrer" className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555] hover:text-[#5D3FD3] truncate">view on etsy →</a>
                    </label>
                ))}
            </div>
        </div>
    );
}


function SiteEditor({ artistsList }) {
    const { content, refresh } = useSiteContent();
    const [draft, setDraft] = useState({});
    const [saving, setSaving] = useState(false);
    const [sub, setSub] = useState("content"); // content | nav | sections

    useEffect(() => {
        setDraft({ ...content });
    }, [content]);

    const setVal = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

    // Immediate save (used by section-flag toggles so unchecking is instant, no need to hit "save all")
    const saveOne = async (key, value) => {
        setDraft((d) => ({ ...d, [key]: value }));
        try {
            await api.put("/site-content", { [key]: value });
            await refresh();
            toast.success(`${key} = ${value}`, { duration: 1500 });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "save failed");
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/site-content", draft);
            await refresh();
            toast.success("saved. cambios visibles al instante (recarga otras pestañas si tenías el sitio abierto).");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "save failed");
        }
        setSaving(false);
    };

    // AI auto-translate: fill every empty _{to} field from its filled _{from} sibling.
    // Uses the backend /admin/translate endpoint (Claude Sonnet 5 via Universal Key).
    const autoTranslate = async (fromLang, toLang) => {
        const fromSuffix = `_${fromLang}`;
        const toSuffix = `_${toLang}`;
        // Find pairs where the FROM field has content and the TO field is empty.
        const pairs = [];
        for (const key of Object.keys(draft)) {
            if (!key.endsWith(fromSuffix)) continue;
            const base = key.slice(0, -fromSuffix.length);
            const toKey = base + toSuffix;
            const src = String(draft[key] || "").trim();
            const dst = String(draft[toKey] || "").trim();
            if (src && !dst) pairs.push({ fromKey: key, toKey, text: src });
        }
        if (pairs.length === 0) {
            toast.info(`no missing ${toLang.toUpperCase()} fields to fill.`);
            return;
        }
        const tid = toast.loading(`translating ${pairs.length} field(s) → ${toLang.toUpperCase()}…`);
        try {
            const patch = {};
            for (const p of pairs) {
                try {
                    const r = await api.post("/admin/translate", { text: p.text, from_lang: fromLang, to_lang: toLang });
                    if (r.data && r.data.text) patch[p.toKey] = r.data.text;
                } catch (_) { /* skip on error, continue */ }
            }
            const filled = Object.keys(patch).length;
            if (filled === 0) {
                toast.error("translation failed for all fields.", { id: tid });
                return;
            }
            setDraft((d) => ({ ...d, ...patch }));
            // Persist immediately so the fill isn't lost on refresh.
            await api.put("/site-content", patch);
            await refresh();
            toast.success(`filled ${filled} ${toLang.toUpperCase()} field(s). remember to review the translations.`, { id: tid, duration: 4000 });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "auto-translate failed", { id: tid });
        }
    };

    return (
        <div data-testid="admin-site-editor" className="max-w-4xl">
            <div className="flex flex-wrap gap-2 items-center mb-6">
                {[["content","copy / texts"], ["nav","menu links"], ["sections","home sections"], ["pages","pages (theme)"], ["announce","announce bar"], ["categories","categories"], ["hero","hero"], ["artists-page","artists page"], ["events-page","events (página)"], ["shop-front","tienda (front)"], ["shop-page","tienda (página)"], ["media","media player"], ["after-dark","after dark"], ["about","about"], ["contact","contact"], ["join","join / subscribe"], ["footer","footer"], ["statement","member statement"], ["invites","invites"], ["subscribers","subscribers"], ["members","members"], ["projects","projects"], ["finance","finance"]].map(([k, label]) => (
                    <button
                        key={k}
                        onClick={() => setSub(k)}
                        data-testid={`site-sub-${k}`}
                        className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-[#333] transition-colors ${sub === k ? "bg-[#333] text-[#FFFFFF]" : "hover:bg-[#333] hover:text-[#FFFFFF]"}`}
                    >
                        {label}
                    </button>
                ))}
                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="site-save"
                    className="ml-auto inline-flex items-center gap-2 bg-[#333] text-[#FFFFFF] hover:bg-[#5D3FD3] hover:text-[#333] transition-colors px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] disabled:opacity-60"
                >
                    <Save size={12} /> {saving ? "saving…" : "save all"}
                </button>
            </div>

            {/* AI auto-translate — scans all _en/_es pairs and fills whichever side is empty.
                Uses Claude Sonnet 5 via the Emergent Universal Key. */}
            <div className="mb-6 border border-[#DDD] p-3 rounded flex flex-wrap items-center gap-3" data-testid="admin-auto-translate">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5D3FD3]">AI auto-translate</div>
                <div className="font-mono text-[10px] text-[#555] flex-1 min-w-[220px]">
                    fills empty language fields from the filled counterpart. review before publishing — machine translations aren&apos;t perfect.
                </div>
                <button
                    onClick={() => autoTranslate("en", "es")}
                    data-testid="auto-translate-en-to-es"
                    className="font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border border-[#333] hover:bg-[#333] hover:text-[#FFFFFF] transition-colors"
                >
                    fill missing ES ← EN
                </button>
                <button
                    onClick={() => autoTranslate("es", "en")}
                    data-testid="auto-translate-es-to-en"
                    className="font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border border-[#333] hover:bg-[#333] hover:text-[#FFFFFF] transition-colors"
                >
                    fill missing EN ← ES
                </button>
            </div>

            {/* Global page width — the whole page sits in a centered container.
                Narrower = more empty margin on the sides. Wider = content stretches
                closer to the browser edges. Applies to every route. */}
            <div className="mb-6 border border-[#DDD] p-3 rounded" data-testid="admin-global-spacing">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5D3FD3] mb-1">page margins</div>
                <div className="font-mono text-[10px] text-[#555] mb-3">controls the left/right whitespace of the whole page. narrower = more margin.</div>
                <label className="block max-w-md">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">page width preset</span>
                    <select
                        value={draft.global_page_width || "default"}
                        onChange={(e) => setVal("global_page_width", e.target.value)}
                        data-testid="global-page-width"
                        className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                    >
                        <option value="xnarrow">x-narrow (max-w-3xl · biggest margins)</option>
                        <option value="narrow">narrow (max-w-5xl)</option>
                        <option value="default">default (max-w-6xl)</option>
                        <option value="wide">wide (max-w-7xl)</option>
                        <option value="xwide">x-wide (max-w-[90rem])</option>
                        <option value="full">full bleed (edge-to-edge · no margins)</option>
                    </select>
                </label>
            </div>

            {sub === "content" && (
                <div className="space-y-10">
                    {/* Visuals: hero image + featured artist picker */}
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-4">visuals</div>
                        <div className="space-y-4">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">hero image url (top-right of home)</span>
                                <input
                                    value={draft.hero_image_url || ""}
                                    onChange={(e) => setVal("hero_image_url", e.target.value)}
                                    data-testid="content-hero-image-url"
                                    placeholder="https://..."
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                />
                                {draft.hero_image_url && (
                                    <img src={draft.hero_image_url} alt="hero preview" className="mt-2 w-40 h-24 object-cover border border-[#333]" />
                                )}
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">hero video url (optional) &mdash; youtube / vimeo / iframe html. If set, appears in the hero right column</span>
                                <input
                                    value={draft.hero_video_url || ""}
                                    onChange={(e) => setVal("hero_video_url", e.target.value)}
                                    data-testid="content-hero-video-url"
                                    placeholder="https://www.youtube.com/watch?v=... or full <iframe>"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">store external url (used by header &ldquo;SHOP &rarr;&rdquo; button and shop-page &ldquo;visit full store&rdquo; cta)</span>
                                <input
                                    value={draft.store_external_url || ""}
                                    onChange={(e) => setVal("store_external_url", e.target.value)}
                                    data-testid="content-store-external-url"
                                    placeholder="https://your-store.myshopify.com  or  https://itzkatu.etsy.com"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">featured artist (big card on home) &mdash; for MULTIPLE artists, toggle &ldquo;&star; home featured artist&rdquo; on each artist in the Artists tab instead</span>
                                <select
                                    value={draft.home_featured_artist_slug || ""}
                                    onChange={(e) => setVal("home_featured_artist_slug", e.target.value)}
                                    data-testid="content-featured-artist"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                >
                                    <option value="">— auto (uses ★ home featured checkbox on artists) —</option>
                                    {(artistsList || []).map((a) => (
                                        <option key={a.id} value={a.slug}>{a.name} · {a.discipline}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    </div>

                    {/* Footer links */}
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-4">footer links</div>
                        <div className="space-y-4">
                            {FOOTER_KEYS.map(([k, label]) => (
                                <label key={k} className="block">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">{label}</span>
                                    <input
                                        value={draft[k] || ""}
                                        onChange={(e) => setVal(k, e.target.value)}
                                        data-testid={`footer-${k}`}
                                        placeholder={k === "footer_email" ? "hello@itzkatu.studio" : "https://…"}
                                        className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                    />
                                </label>
                            ))}
                        </div>
                    </div>

                    {CONTENT_GROUPS.map((g) => (
                        <div key={g.title}>
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-4">{g.title}</div>
                            <div className="space-y-6">
                                {g.keys.map((k) => (
                                    <div key={k} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <label className="block">
                                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">{k} · EN</span>
                                            <textarea
                                                value={draft[`${k}_en`] || ""}
                                                onChange={(e) => setVal(`${k}_en`, e.target.value)}
                                                data-testid={`content-${k}-en`}
                                                rows={k.startsWith("manifesto_body") || k === "hero_tag" ? 3 : 1}
                                                className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                            />
                                        </label>
                                        <label className="block">
                                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">{k} · ES</span>
                                            <textarea
                                                value={draft[`${k}_es`] || ""}
                                                onChange={(e) => setVal(`${k}_es`, e.target.value)}
                                                data-testid={`content-${k}-es`}
                                                rows={k.startsWith("manifesto_body") || k === "hero_tag" ? 3 : 1}
                                                className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                            />
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {sub === "nav" && (
                <div className="space-y-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">menu links</div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                        label = text shown in header + footer. slug = url path (e.g. <b>experimentos</b> → <b>/experimentos</b>). leave slug blank to keep the default. es &amp; en share the same slug for now.
                    </p>
                    <div className="p-4 bg-[#F5F0E8] border-l-2 border-[#5D3FD3] mb-2" data-testid="admin-nav-scheme">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-2">nav bar color scheme</div>
                        <p className="font-mono text-[10px] text-[#555] mb-2">
                            override the nav bar colors (falls back to the page scheme when set to <b>inherit</b>).
                        </p>
                        <SchemePicker
                            scheme={draft.section_scheme_nav || ""}
                            onChange={(v) => setVal("section_scheme_nav", v)}
                            label="nav scheme (inherit = follows page)"
                            testId="nav-scheme"
                        />
                    </div>
                    <div className="p-4 bg-[#F5F0E8] border-l-2 border-[#5D3FD3] mb-2" data-testid="admin-nav-border">
                        <label className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] cursor-pointer">
                            <input
                                type="checkbox"
                                checked={draft.nav_border_bottom === "1"}
                                onChange={(e) => saveOne("nav_border_bottom", e.target.checked ? "1" : "0")}
                                data-testid="nav-border-toggle"
                            />
                            show a 1px bottom border under the nav bar
                        </label>
                    </div>
                    {/* Typography sizes for the nav — brand (itzkatu wordmark) + link labels. */}
                    <div className="p-4 bg-[#F5F0E8] border-l-2 border-[#5D3FD3] mb-2 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-nav-typography">
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">brand size (ITZKATU wordmark)</span>
                            <select
                                value={draft.nav_brand_size || "md"}
                                onChange={(e) => setVal("nav_brand_size", e.target.value)}
                                data-testid="nav-brand-size"
                                className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                            >
                                <option value="sm">small</option>
                                <option value="md">medium (default)</option>
                                <option value="lg">large</option>
                                <option value="xl">x-large</option>
                                <option value="xxl">xx-large</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">link size (menu labels)</span>
                            <select
                                value={draft.nav_link_size || "sm"}
                                onChange={(e) => setVal("nav_link_size", e.target.value)}
                                data-testid="nav-link-size"
                                className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                            >
                                <option value="xs">x-small</option>
                                <option value="sm">small (default)</option>
                                <option value="md">medium</option>
                                <option value="lg">large</option>
                                <option value="xl">x-large</option>
                            </select>
                        </label>
                    </div>
                    <table className="w-full border border-[#333]">
                        <thead className="bg-[#333] text-[#FFFFFF] font-mono text-[10px] uppercase tracking-[0.2em]">
                            <tr>
                                <th className="p-3 text-left">key</th>
                                <th className="p-3 text-left">label EN</th>
                                <th className="p-3 text-left">label ES</th>
                                <th className="p-3 text-left">slug (url path)</th>
                                <th className="p-3 text-left">show?</th>
                            </tr>
                        </thead>
                        <tbody className="font-mono text-sm">
                            {NAV_KEYS.map((k) => (
                                <tr key={k} className="border-t border-[#EEE]">
                                    <td className="p-3 uppercase tracking-[0.15em] text-[#555]">{k}</td>
                                    <td className="p-3">
                                        <input
                                            value={draft[`nav_label_${k}_en`] || ""}
                                            onChange={(e) => setVal(`nav_label_${k}_en`, e.target.value)}
                                            data-testid={`nav-label-${k}-en`}
                                            className="w-full bg-transparent focus:border-[#5D3FD3] outline-none py-1"
                                        />
                                    </td>
                                    <td className="p-3">
                                        <input
                                            value={draft[`nav_label_${k}_es`] || ""}
                                            onChange={(e) => setVal(`nav_label_${k}_es`, e.target.value)}
                                            data-testid={`nav-label-${k}-es`}
                                            className="w-full bg-transparent focus:border-[#5D3FD3] outline-none py-1"
                                        />
                                    </td>
                                    <td className="p-3">
                                        {k === "index" ? (
                                            <span className="text-[#999] font-mono text-xs">/</span>
                                        ) : (
                                            <div className="flex items-center gap-1">
                                                <span className="text-[#999] font-mono text-xs">/</span>
                                                <input
                                                    value={draft[`nav_slug_${k}`] || ""}
                                                    onChange={(e) => setVal(`nav_slug_${k}`, e.target.value.replace(/^\/+/, "").replace(/[^a-z0-9-]/gi, "-").toLowerCase())}
                                                    data-testid={`nav-slug-${k}`}
                                                    placeholder={k}
                                                    className="w-full bg-transparent border-b border-[#333]/25 focus:border-[#5D3FD3] outline-none py-1 font-mono text-xs"
                                                />
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-3">
                                        <input
                                            type="checkbox"
                                            data-testid={`nav-show-${k}`}
                                            checked={draft[`nav_show_${k}`] === "1"}
                                            onChange={(e) => setVal(`nav_show_${k}`, e.target.checked ? "1" : "0")}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {sub === "sections" && (
                <div className="space-y-8 max-w-3xl">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-3">order + visibility</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] mb-4">
                            drag order with the up/down arrows. toggle visibility, subtitle (kicker), and big title for each section.
                        </p>
                        <label className="flex items-center gap-3 p-3 mb-5 border-2 border-[#5D3FD3] font-mono text-[11px] tracking-[0.05em] cursor-pointer" data-testid="effects-inherit-switch">
                            <input
                                type="checkbox"
                                checked={draft.effects_inherit_scheme !== "0"}
                                onChange={(e) => saveOne("effects_inherit_scheme", e.target.checked ? "1" : "0")}
                            />
                            <span><b className="uppercase">effects inherit color</b> — ON: every image effect (scene, radio, artists, featured…) is tinted with the section/page palette. OFF: effects keep the ORIGINAL image colors.</span>
                        </label>
                        {(() => {
                            const orderStr = draft.home_section_order || "shop,featured_artist,artists,events,manifesto";
                            const order = orderStr.split(",").map((s) => s.trim()).filter(Boolean);
                            const defaults = SECTION_KEYS.map((s) => s[2]);
                            const inOrder = new Set(order);
                            const finalOrder = [...order, ...defaults.filter((d) => !inOrder.has(d))];
                            const move = (idx, dir) => {
                                const next = [...finalOrder];
                                const j = idx + dir;
                                if (j < 0 || j >= next.length) return;
                                [next[idx], next[j]] = [next[j], next[idx]];
                                setVal("home_section_order", next.join(","));
                            };
                            return (
                                <ul className="border border-[#333]">
                                    {finalOrder.map((key, idx) => {
                                        const spec = SECTION_KEYS.find((s) => s[2] === key);
                                        if (!spec) return null;
                                        const [flag, label] = spec;
                                        return (
                                            <li key={key} className="grid grid-cols-12 items-center gap-3 p-4 border-b border-[#EEE] last:border-b-0">
                                                <div className="col-span-1 flex flex-col gap-1">
                                                    <button
                                                        onClick={() => move(idx, -1)}
                                                        disabled={idx === 0}
                                                        data-testid={`section-order-up-${key}`}
                                                        className="border border-[#333] w-6 h-5 text-[10px] disabled:opacity-30"
                                                    >
                                                        ↑
                                                    </button>
                                                    <button
                                                        onClick={() => move(idx, 1)}
                                                        disabled={idx === finalOrder.length - 1}
                                                        data-testid={`section-order-down-${key}`}
                                                        className="border border-[#333] w-6 h-5 text-[10px] disabled:opacity-30"
                                                    >
                                                        ↓
                                                    </button>
                                                </div>
                                                <div className="col-span-3">
                                                    <div className="font-heading font-bold uppercase tracking-tight text-sm">{label}</div>
                                                    <div className="font-mono text-[9px] uppercase text-[#777]">{key}</div>
                                                </div>
                                                <label className="col-span-2 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em]">
                                                    <input
                                                        type="checkbox"
                                                        data-testid={`section-${flag}`}
                                                        checked={draft[flag] !== "0"}
                                                        onChange={(e) => saveOne(flag, e.target.checked ? "1" : "0")}
                                                    />
                                                    show
                                                </label>
                                                <label className="col-span-2 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em]">
                                                    <input
                                                        type="checkbox"
                                                        data-testid={`section-kicker-${key}`}
                                                        checked={draft[`section_show_kicker_${key}`] !== "0"}
                                                        onChange={(e) => saveOne(`section_show_kicker_${key}`, e.target.checked ? "1" : "0")}
                                                    />
                                                    subtitle
                                                </label>
                                                {key !== "manifesto" ? (
                                                    <label className="col-span-2 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em]">
                                                        <input
                                                            type="checkbox"
                                                            data-testid={`section-title-${key}`}
                                                            checked={draft[`section_show_title_${key}`] !== "0"}
                                                            onChange={(e) => saveOne(`section_show_title_${key}`, e.target.checked ? "1" : "0")}
                                                        />
                                                        title
                                                    </label>
                                                ) : <div className="col-span-2" />}
                                                <div className="col-span-2 flex gap-1">
                                                    {key !== "manifesto" ? (
                                                        <>
                                                            <input
                                                                value={draft[`title_${key}_en`] || ""}
                                                                onChange={(e) => setVal(`title_${key}_en`, e.target.value)}
                                                                placeholder="title EN"
                                                                data-testid={`section-title-en-${key}`}
                                                                className="w-full bg-transparent border border-[#333] font-mono text-[10px] uppercase p-1 focus:border-[#5D3FD3] outline-none"
                                                            />
                                                            <input
                                                                value={draft[`title_${key}_es`] || ""}
                                                                onChange={(e) => setVal(`title_${key}_es`, e.target.value)}
                                                                placeholder="ES"
                                                                data-testid={`section-title-es-${key}`}
                                                                className="w-full bg-transparent border border-[#333] font-mono text-[10px] uppercase p-1 focus:border-[#5D3FD3] outline-none"
                                                            />
                                                        </>
                                                    ) : null}
                                                </div>

                                                {/* Second row: kicker text EN/ES for this section */}
                                                {KICKER_KEY[key] && (
                                                    <div className="col-span-12 grid grid-cols-12 gap-3 pt-2 mt-1 border-t border-[#EEE]">
                                                        <div className="col-span-1" />
                                                        <div className="col-span-3 font-mono text-[9px] uppercase tracking-[0.25em] text-[#555] pt-2">
                                                            kicker text ({KICKER_KEY[key]})
                                                        </div>
                                                        <div className="col-span-4">
                                                            <input
                                                                value={draft[`${KICKER_KEY[key]}_en`] || ""}
                                                                onChange={(e) => setVal(`${KICKER_KEY[key]}_en`, e.target.value)}
                                                                placeholder={`kicker EN (e.g. 02 / next)`}
                                                                data-testid={`section-kicker-en-${key}`}
                                                                className="w-full bg-transparent border border-[#333] font-mono text-[10px] p-1.5 focus:border-[#5D3FD3] outline-none"
                                                            />
                                                        </div>
                                                        <div className="col-span-4">
                                                            <input
                                                                value={draft[`${KICKER_KEY[key]}_es`] || ""}
                                                                onChange={(e) => setVal(`${KICKER_KEY[key]}_es`, e.target.value)}
                                                                placeholder={`kicker ES (ej. 02 / próximas)`}
                                                                data-testid={`section-kicker-es-${key}`}
                                                                className="w-full bg-transparent border border-[#333] font-mono text-[10px] p-1.5 focus:border-[#5D3FD3] outline-none"
                                                            />
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Third row: body / description text (paragraph shown under the title) */}
                                                <div className="col-span-12 grid grid-cols-12 gap-3 pt-2 mt-1 border-t border-[#EEE]">
                                                    <div className="col-span-1" />
                                                    <div className="col-span-3 font-mono text-[9px] uppercase tracking-[0.25em] text-[#555] pt-2">
                                                        body text (body_{key}_lang)
                                                    </div>
                                                    <div className="col-span-4">
                                                        <textarea
                                                            rows={2}
                                                            value={draft[`body_${key}_en`] || ""}
                                                            onChange={(e) => setVal(`body_${key}_en`, e.target.value)}
                                                            placeholder="body / description EN"
                                                            data-testid={`section-body-en-${key}`}
                                                            className="w-full bg-transparent border border-[#333] font-mono text-[10px] p-1.5 focus:border-[#5D3FD3] outline-none"
                                                        />
                                                    </div>
                                                    <div className="col-span-4">
                                                        <textarea
                                                            rows={2}
                                                            value={draft[`body_${key}_es`] || ""}
                                                            onChange={(e) => setVal(`body_${key}_es`, e.target.value)}
                                                            placeholder="body / description ES"
                                                            data-testid={`section-body-es-${key}`}
                                                            className="w-full bg-transparent border border-[#333] font-mono text-[10px] p-1.5 focus:border-[#5D3FD3] outline-none"
                                                        />
                                                    </div>
                                                </div>

                                {/* Editorial banner extras: featured artist */}
                                {key === "editorial" && (
                                    <div className="col-span-12 grid grid-cols-12 gap-3 pt-2 mt-1 border-t border-[#EEE]">
                                        <div className="col-span-1" />
                                        <div className="col-span-4 font-mono text-[9px] uppercase tracking-[0.25em] text-[#555] pt-2">
                                            featured artist (slug) — empty or &quot;random&quot; = random pick
                                        </div>
                                        <div className="col-span-7">
                                            <input
                                                value={draft.editorial_artist_slug || ""}
                                                onChange={(e) => setVal("editorial_artist_slug", e.target.value)}
                                                placeholder="e.g. deuteroz · empty = random"
                                                data-testid="editorial-artist-slug"
                                                className="w-full bg-transparent border border-[#333] font-mono text-[10px] p-1.5 focus:border-[#5D3FD3] outline-none"
                                            />
                                        </div>
                                    </div>
                                )}

                                                {/* Fourth row: section color scheme (preset only — no raw hex). */}
                                                <div className="col-span-12 grid grid-cols-12 gap-3 pt-2 mt-1 border-t border-[#EEE]">
                                                    <div className="col-span-1" />
                                                    <div className="col-span-3 font-mono text-[9px] uppercase tracking-[0.25em] text-[#555] pt-2">
                                                        color scheme
                                                    </div>
                                                    <div className="col-span-8 flex flex-wrap gap-2 items-center">
                                                        {SCHEME_ORDER.map((sch) => {
                                                            const active = ((draft[`section_scheme_${key}`] || "").toLowerCase()) === sch;
                                                            const sw = SCHEME_SWATCHES[sch] || { bg: "#FFF", fg: "#111" };
                                                            return (
                                                                <button
                                                                    key={sch || "inherit"}
                                                                    type="button"
                                                                    onClick={() => saveOne(`section_scheme_${key}`, sch)}
                                                                    data-testid={`section-scheme-${key}-${sch || "inherit"}`}
                                                                    className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${active ? "border-[#5D3FD3] text-[#5D3FD3]" : "border-[#333] text-[#333] hover:border-[#5D3FD3]"}`}
                                                                >
                                                                    {sch === "" ? (
                                                                        <span className="inline-block w-3 h-3 border border-[#00000022]" style={{ backgroundImage: "linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%)", backgroundSize: "6px 6px", backgroundPosition: "0 0, 3px 3px" }} />
                                                                    ) : (
                                                                        <>
                                                                            <span className="inline-block w-3 h-3 border border-[#00000022]" style={{ background: sw.bg }} />
                                                                            <span className="inline-block w-2 h-3" style={{ background: sw.fg }} />
                                                                        </>
                                                                    )}
                                                                    {sch || "inherit"}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            );
                        })()}
                    </div>

                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-3">hero subtitle</div>
                        <label className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em]">
                            <input
                                type="checkbox"
                                data-testid="section-kicker-hero"
                                checked={draft.section_show_kicker_hero !== "0"}
                                onChange={(e) => setVal("section_show_kicker_hero", e.target.checked ? "1" : "0")}
                            />
                            show &ldquo;creative collective&rdquo; kicker in hero
                        </label>
                    </div>

                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                        note: to pick which artist appears in the big &ldquo;featured artist&rdquo; card, edit an artist and toggle &ldquo;home featured artist&rdquo;. Multiple can be featured at once.
                    </p>
                </div>
            )}

            {sub === "carousel" && (
                <div className="space-y-6 max-w-3xl">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-2">hero spotlight carousel</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed mb-6">
                            up to 3 spotlight slots shown on the hero. any slot with a title or image is included. auto-advance every 6s + prev/next arrows.
                            for links, use internal paths (e.g. <code>/artists/deuteroz</code>, <code>/shop</code>, <code>/events</code>) or full external urls.
                        </p>
                        {[1, 2, 3].map((n) => (
                            <div key={n} className="border border-[#333] p-4 mb-4">
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5D3FD3] mb-3">slot {n}</div>
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">title · EN</span>
                                        <input
                                            value={draft[`hero_slot_${n}_title_en`] ?? draft[`hero_slot_${n}_title`] ?? ""}
                                            onChange={(e) => setVal(`hero_slot_${n}_title_en`, e.target.value)}
                                            data-testid={`carousel-slot-${n}-title-en`}
                                            placeholder="e.g. featured artist — deuteroz"
                                            className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">title · ES</span>
                                        <input
                                            value={draft[`hero_slot_${n}_title_es`] ?? draft[`hero_slot_${n}_title`] ?? ""}
                                            onChange={(e) => setVal(`hero_slot_${n}_title_es`, e.target.value)}
                                            data-testid={`carousel-slot-${n}-title-es`}
                                            placeholder="ej. artista destacado — deuteroz"
                                            className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">subtitle · EN</span>
                                        <input
                                            value={draft[`hero_slot_${n}_subtitle_en`] ?? draft[`hero_slot_${n}_subtitle`] ?? ""}
                                            onChange={(e) => setVal(`hero_slot_${n}_subtitle_en`, e.target.value)}
                                            data-testid={`carousel-slot-${n}-subtitle-en`}
                                            placeholder="e.g. producer · new track out now"
                                            className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">subtitle · ES</span>
                                        <input
                                            value={draft[`hero_slot_${n}_subtitle_es`] ?? draft[`hero_slot_${n}_subtitle`] ?? ""}
                                            onChange={(e) => setVal(`hero_slot_${n}_subtitle_es`, e.target.value)}
                                            data-testid={`carousel-slot-${n}-subtitle-es`}
                                            placeholder="ej. productor · nuevo track disponible"
                                            className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                        />
                                    </label>
                                    <label className="block col-span-2">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">image url</span>
                                        <input
                                            value={draft[`hero_slot_${n}_image_url`] || ""}
                                            onChange={(e) => setVal(`hero_slot_${n}_image_url`, e.target.value)}
                                            data-testid={`carousel-slot-${n}-image`}
                                            placeholder="https://..."
                                            className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                        />
                                    </label>
                                    <label className="block col-span-2">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">link (internal path or external url) &mdash; optional</span>
                                        <input
                                            value={draft[`hero_slot_${n}_link`] || ""}
                                            onChange={(e) => setVal(`hero_slot_${n}_link`, e.target.value)}
                                            data-testid={`carousel-slot-${n}-link`}
                                            placeholder="/artists/deuteroz  or  https://..."
                                            className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                        />
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {sub === "contact" && (
                <div className="space-y-6 max-w-4xl">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">contact page</div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                        top text is edited in <b>copy / texts</b> (search for <code>contact_h</code> and <code>contact_intro</code>). The rows below are the big list of contact methods. Any row without a label or value is hidden.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">page title · EN</span>
                            <input value={draft.contact_h_en || ""} onChange={(e) => setVal("contact_h_en", e.target.value)} data-testid="contact-h-en" placeholder="get in touch." className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">page title · ES</span>
                            <input value={draft.contact_h_es || ""} onChange={(e) => setVal("contact_h_es", e.target.value)} data-testid="contact-h-es" placeholder="hablemos." className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">intro paragraph · EN</span>
                            <textarea value={draft.contact_intro_en || ""} onChange={(e) => setVal("contact_intro_en", e.target.value)} data-testid="contact-intro-en" rows="2" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">intro paragraph · ES</span>
                            <textarea value={draft.contact_intro_es || ""} onChange={(e) => setVal("contact_intro_es", e.target.value)} data-testid="contact-intro-es" rows="2" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                    </div>
                    <div className="pt-4">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-3">contact rows</div>
                        <div className="space-y-4">
                            {[1, 2, 3, 4, 5, 6].map((n) => (
                                <div key={n} className="border border-[#333]/15 p-4 space-y-3">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5D3FD3]">row {n}</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <label className="block">
                                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">label · EN</span>
                                            <input value={draft[`contact_row_${n}_label_en`] || ""} onChange={(e) => setVal(`contact_row_${n}_label_en`, e.target.value)} data-testid={`contact-row-${n}-label-en`} placeholder="bookings + general" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                                        </label>
                                        <label className="block">
                                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">label · ES</span>
                                            <input value={draft[`contact_row_${n}_label_es`] || ""} onChange={(e) => setVal(`contact_row_${n}_label_es`, e.target.value)} data-testid={`contact-row-${n}-label-es`} placeholder="bookings + general" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                                        </label>
                                    </div>
                                    <label className="block">
                                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">value (what visitors see — email, handle, etc)</span>
                                        <input value={draft[`contact_row_${n}_value`] || ""} onChange={(e) => setVal(`contact_row_${n}_value`, e.target.value)} data-testid={`contact-row-${n}-value`} placeholder="hello@itzkatu.studio" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                                    </label>
                                    <label className="block">
                                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">link (mailto:, https://, or leave empty)</span>
                                        <input value={draft[`contact_row_${n}_href`] || ""} onChange={(e) => setVal(`contact_row_${n}_href`, e.target.value)} data-testid={`contact-row-${n}-href`} placeholder="mailto:hello@itzkatu.studio" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {sub === "about" && (
                <div className="space-y-6 max-w-3xl">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">about / quiénes somos</div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                        edita el kicker, el título grande, el intro y el body del /about y de la sección &quot;about the collective&quot; en el home.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">kicker (small label above title) · EN</span>
                            <input value={draft.about_kicker_en || ""} onChange={(e) => setVal("about_kicker_en", e.target.value)} data-testid="about-kicker-en" placeholder="about the collective" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">kicker · ES</span>
                            <input value={draft.about_kicker_es || ""} onChange={(e) => setVal("about_kicker_es", e.target.value)} data-testid="about-kicker-es" placeholder="sobre el colectivo" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">page title · EN</span>
                            <input value={draft.about_title_en || ""} onChange={(e) => setVal("about_title_en", e.target.value)} data-testid="about-h-en" placeholder="who we are." className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">page title · ES</span>
                            <input value={draft.about_title_es || ""} onChange={(e) => setVal("about_title_es", e.target.value)} data-testid="about-h-es" placeholder="quiénes somos." className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">intro paragraph · EN</span>
                            <textarea value={draft.about_intro_en || ""} onChange={(e) => setVal("about_intro_en", e.target.value)} data-testid="about-intro-en" rows="3" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">intro paragraph · ES</span>
                            <textarea value={draft.about_intro_es || ""} onChange={(e) => setVal("about_intro_es", e.target.value)} data-testid="about-intro-es" rows="3" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">body (long text) · EN</span>
                            <textarea value={draft.about_body_en || ""} onChange={(e) => setVal("about_body_en", e.target.value)} data-testid="about-body-en" rows="6" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">body (long text) · ES</span>
                            <textarea value={draft.about_body_es || ""} onChange={(e) => setVal("about_body_es", e.target.value)} data-testid="about-body-es" rows="6" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                    </div>
                </div>
            )}

            {sub === "invites" && <InvitesEditor />}
            {sub === "subscribers" && <SubscribersEditor />}
            {sub === "members" && <MembersEditor />}
            {sub === "projects" && <ProjectsEditor />}
            {sub === "finance" && <FinanceEditor />}

            {sub === "join" && (
                <div className="space-y-6 max-w-3xl">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">join / newsletter</div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                        two blocks on the home JOIN section:<br/>
                        <b>apply block</b> (people who want to enter the collective) → posts to <code>/api/interest</code> with source <code>home_general</code>.<br/>
                        <b>newsletter block</b> (just updates) → posts to <code>/api/subscribe</code>. See in <b>subscribers</b> tab.
                    </p>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] pt-4">apply — quiero entrar</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">kicker · EN</span>
                            <input value={draft.join_kicker_en || ""} onChange={(e) => setVal("join_kicker_en", e.target.value)} data-testid="join-kicker-en" placeholder="we're open" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">kicker · ES</span>
                            <input value={draft.join_kicker_es || ""} onChange={(e) => setVal("join_kicker_es", e.target.value)} data-testid="join-kicker-es" placeholder="abrimos convocatoria" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">title · EN</span>
                            <input value={draft.join_title_en || ""} onChange={(e) => setVal("join_title_en", e.target.value)} data-testid="join-title-en" placeholder="let me in." className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">title · ES</span>
                            <input value={draft.join_title_es || ""} onChange={(e) => setVal("join_title_es", e.target.value)} data-testid="join-title-es" placeholder="quiero entrar." className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block md:col-span-2"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">body · EN</span>
                            <textarea value={draft.join_body_en || ""} onChange={(e) => setVal("join_body_en", e.target.value)} data-testid="join-body-en" rows="3" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block md:col-span-2"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">body · ES</span>
                            <textarea value={draft.join_body_es || ""} onChange={(e) => setVal("join_body_es", e.target.value)} data-testid="join-body-es" rows="3" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">button label · EN</span>
                            <input value={draft.join_cta_en || ""} onChange={(e) => setVal("join_cta_en", e.target.value)} data-testid="join-cta-en" placeholder="send" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                        <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">button label · ES</span>
                            <input value={draft.join_cta_es || ""} onChange={(e) => setVal("join_cta_es", e.target.value)} data-testid="join-cta-es" placeholder="enviar" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                        </label>
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] pt-6">subscribe — newsletter</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">kicker · EN</span>
                            <input value={draft.subscribe_kicker_en || ""} onChange={(e) => setVal("subscribe_kicker_en", e.target.value)} placeholder="newsletter" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2" />
                        </label>
                        <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">kicker · ES</span>
                            <input value={draft.subscribe_kicker_es || ""} onChange={(e) => setVal("subscribe_kicker_es", e.target.value)} placeholder="boletín" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2" />
                        </label>
                        <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">title · EN</span>
                            <input value={draft.subscribe_title_en || ""} onChange={(e) => setVal("subscribe_title_en", e.target.value)} placeholder="just stay in the loop." className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2" />
                        </label>
                        <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">title · ES</span>
                            <input value={draft.subscribe_title_es || ""} onChange={(e) => setVal("subscribe_title_es", e.target.value)} placeholder="solo mantenerte al día." className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2" />
                        </label>
                        <label className="block md:col-span-2"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">body · EN</span>
                            <input value={draft.subscribe_body_en || ""} onChange={(e) => setVal("subscribe_body_en", e.target.value)} placeholder="drops, events, announcements. no spam." className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2" />
                        </label>
                        <label className="block md:col-span-2"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">body · ES</span>
                            <input value={draft.subscribe_body_es || ""} onChange={(e) => setVal("subscribe_body_es", e.target.value)} placeholder="drops, eventos, avisos. sin spam." className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2" />
                        </label>
                        <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">button label · EN</span>
                            <input value={draft.subscribe_cta_en || ""} onChange={(e) => setVal("subscribe_cta_en", e.target.value)} placeholder="subscribe" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2" />
                        </label>
                        <label className="block"><span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">button label · ES</span>
                            <input value={draft.subscribe_cta_es || ""} onChange={(e) => setVal("subscribe_cta_es", e.target.value)} placeholder="suscribirme" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2" />
                        </label>
                    </div>
                </div>
            )}

            {sub === "footer" && (
                <div className="space-y-8 max-w-3xl">
                    {/* ---- Big title / subtitle / extra copy (opt-in) ---- */}
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-3">footer · big title + subtitle + extra copy</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed mb-3">
                            estos bloques están apagados por default. actívalos y pon el texto para que aparezcan arriba de los links en el footer.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pb-4 border-b border-[#DDD]">
                            <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                                <input type="checkbox" checked={draft.footer_show_title === "1"} onChange={(e) => setVal("footer_show_title", e.target.checked ? "1" : "0")} data-testid="footer-show-title" />
                                show big title
                            </label>
                            <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                                <input type="checkbox" checked={draft.footer_show_subtitle === "1"} onChange={(e) => setVal("footer_show_subtitle", e.target.checked ? "1" : "0")} data-testid="footer-show-subtitle" />
                                show subtitle
                            </label>
                            <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                                <input type="checkbox" checked={draft.footer_show_extra === "1"} onChange={(e) => setVal("footer_show_extra", e.target.checked ? "1" : "0")} data-testid="footer-show-extra" />
                                show extra paragraph
                            </label>
                        </div>
                        {[
                            ["footer_title", "big title"],
                            ["footer_subtitle", "subtitle (one line, italic-style)"],
                            ["footer_extra", "extra paragraph (multi-line)"],
                        ].map(([k, label]) => (
                            <div key={k} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <label className="block">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">{label} · EN</span>
                                    <textarea rows={k === "footer_extra" ? 4 : 2} value={draft[`${k}_en`] || ""} onChange={(e) => setVal(`${k}_en`, e.target.value)} data-testid={`footer-${k}-en`} className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                                </label>
                                <label className="block">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">{label} · ES</span>
                                    <textarea rows={k === "footer_extra" ? 4 : 2} value={draft[`${k}_es`] || ""} onChange={(e) => setVal(`${k}_es`, e.target.value)} data-testid={`footer-${k}-es`} className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                                </label>
                            </div>
                        ))}
                    </div>

                    {/* ---- Additional social links (1..4). Icon is auto-detected. ---- */}
                    <div className="border-t border-[#DDD] pt-6">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-3">footer · social / extra links</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed mb-3">
                            hasta 4 URLs adicionales. el icono se detecta automáticamente (instagram, spotify, soundcloud, youtube, twitter/x, telegram, website).
                        </p>
                        {[1, 2, 3, 4].map((n) => (
                            <div key={n} className="mb-3">
                                <label className="block">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">link {n} · full URL</span>
                                    <input
                                        value={draft[`footer_link_${n}_url`] || ""}
                                        onChange={(e) => setVal(`footer_link_${n}_url`, e.target.value)}
                                        data-testid={`footer-link-${n}-url`}
                                        placeholder="https://…"
                                        className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                    />
                                </label>
                            </div>
                        ))}
                    </div>

                    {/* ---- Existing column titles / navigation toggles (unchanged) ---- */}
                    <div className="border-t border-[#DDD] pt-6">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-3">footer column titles</div>
                        {[
                            ["footer_navigate", "navigate title (default: navigate / navegar)"],
                            ["footer_signal", "signal title (default: signal / señal)"],
                            ["footer_rights", "copyright line (bottom bar)"],
                        ].map(([k, label]) => (
                            <div key={k} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <label className="block">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">{label} · EN</span>
                                    <input
                                        value={draft[`${k}_en`] || ""}
                                        onChange={(e) => setVal(`${k}_en`, e.target.value)}
                                        data-testid={`footer-title-${k}-en`}
                                        className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                    />
                                </label>
                                <label className="block">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">{label} · ES</span>
                                    <input
                                        value={draft[`${k}_es`] || ""}
                                        onChange={(e) => setVal(`${k}_es`, e.target.value)}
                                        data-testid={`footer-title-${k}-es`}
                                        className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                    />
                                </label>
                            </div>
                        ))}
                    </div>

                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-3">navigate column — which links to show</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] mb-3">
                            labels come from <b>menu links</b> tab (shared with header).
                        </p>
                        <div className="border border-[#333]">
                            {NAV_KEYS.map((k) => {
                                const val = draft[`footer_show_${k}`];
                                const isOn = val === "1" || val === true;
                                return (
                                    <label key={k} className="flex items-center justify-between p-3 border-b border-[#EEE] last:border-b-0 font-mono text-xs uppercase tracking-[0.15em]">
                                        <span>{k}</span>
                                        <span className="text-[#777] mx-4 flex-1 text-right pr-4 truncate">{draft[`nav_label_${k}_en`] || `(default ${k})`}</span>
                                        <input
                                            type="checkbox"
                                            data-testid={`footer-show-${k}`}
                                            checked={isOn}
                                            onChange={(e) => setVal(`footer_show_${k}`, e.target.checked ? "1" : "0")}
                                        />
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-1">signal column — social + external links (6 slots)</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] mb-3">
                            pick a platform for the icon (instagram, soundcloud, spotify, youtube, tiktok, twitter, twitch, bandcamp, telegram) or leave blank. label overrides the platform name.
                        </p>
                        <div className="space-y-4">
                            {[1, 2, 3, 4, 5, 6].map((n) => (
                                <div key={n} className="border border-[#333]/15 p-3">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                                        <label className="block">
                                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">platform (icon)</span>
                                            <input
                                                value={draft[`footer_social_${n}_platform`] || ""}
                                                onChange={(e) => setVal(`footer_social_${n}_platform`, e.target.value)}
                                                data-testid={`footer-social-${n}-platform`}
                                                placeholder="instagram"
                                                className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                                            />
                                        </label>
                                        <label className="block">
                                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">label · EN</span>
                                            <input
                                                value={draft[`footer_social_${n}_label_en`] || ""}
                                                onChange={(e) => setVal(`footer_social_${n}_label_en`, e.target.value)}
                                                data-testid={`footer-social-${n}-label-en`}
                                                placeholder="(defaults to platform)"
                                                className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                                            />
                                        </label>
                                        <label className="block">
                                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">label · ES</span>
                                            <input
                                                value={draft[`footer_social_${n}_label_es`] || ""}
                                                onChange={(e) => setVal(`footer_social_${n}_label_es`, e.target.value)}
                                                data-testid={`footer-social-${n}-label-es`}
                                                placeholder="(defaults to platform)"
                                                className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                                            />
                                        </label>
                                    </div>
                                    <label className="block">
                                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">url (or plain email)</span>
                                        <input
                                            value={draft[`footer_social_${n}_url`] || ""}
                                            onChange={(e) => setVal(`footer_social_${n}_url`, e.target.value)}
                                            data-testid={`footer-social-${n}-url`}
                                            placeholder="https://…"
                                            className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                                        />
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {sub === "carousel" && null}
            {false && (
                <div />
            )}

            {sub === "announce" && (
                <div className="space-y-6 max-w-3xl">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">announcement bar — thin strip above the header on every page</div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                        toggle it on in <b>home sections → announcement bar</b>. Copy is per language, optionally clickable, and can auto-scroll as a marquee.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">text · EN</span>
                            <input
                                value={draft.announce_text_en || ""}
                                onChange={(e) => setVal("announce_text_en", e.target.value)}
                                data-testid="announce-text-en"
                                placeholder="new drop — deuteroz · sep 26 · listen now"
                                className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                            />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">text · ES</span>
                            <input
                                value={draft.announce_text_es || ""}
                                onChange={(e) => setVal("announce_text_es", e.target.value)}
                                data-testid="announce-text-es"
                                placeholder="nuevo drop — deuteroz · 26 sep · escuchá"
                                className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                            />
                        </label>
                    </div>
                    <label className="block">
                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">link (optional — where clicking sends the user)</span>
                        <input
                            value={draft.announce_link || ""}
                            onChange={(e) => setVal("announce_link", e.target.value)}
                            data-testid="announce-link"
                            placeholder="/shop  or  https://…"
                            className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                        />
                    </label>
                    <label className="block">
                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">size % — 30 = tiny · 100 = default · 200 = double</span>
                        <input
                            type="number"
                            min="30"
                            max="300"
                            step="10"
                            value={draft.announce_size_pct || "100"}
                            onChange={(e) => setVal("announce_size_pct", e.target.value)}
                            data-testid="announce-size-pct"
                            className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                        />
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="announce-marquee-toggle">
                        <input
                            type="checkbox"
                            checked={draft.announce_marquee === "1" || draft.announce_marquee === true}
                            onChange={(e) => setVal("announce_marquee", e.target.checked ? "1" : "0")}
                        />
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#333]">
                            marquee (auto-scroll horizontally)
                        </span>
                    </label>
                </div>
            )}

            {sub === "categories" && (
                <div className="space-y-6 max-w-4xl">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">categories — 6 big-image tiles on the home</div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                        toggle on in <b>home sections → categories grid</b>. Any tile without an image AND without a label is skipped. Common uses: MERCH, MUSIC, EVENTS, ARTISTS, LAB, ARCHIVE.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">section kicker · EN</span>
                            <input
                                value={draft.cats_kicker_en || ""}
                                onChange={(e) => setVal("cats_kicker_en", e.target.value)}
                                data-testid="cats-kicker-en"
                                placeholder="shop"
                                className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                            />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">section kicker · ES</span>
                            <input
                                value={draft.cats_kicker_es || ""}
                                onChange={(e) => setVal("cats_kicker_es", e.target.value)}
                                data-testid="cats-kicker-es"
                                placeholder="explorá"
                                className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                            />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">section title · EN</span>
                            <input
                                value={draft.cats_title_en || ""}
                                onChange={(e) => setVal("cats_title_en", e.target.value)}
                                data-testid="cats-title-en"
                                placeholder="by category."
                                className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                            />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">section title · ES</span>
                            <input
                                value={draft.cats_title_es || ""}
                                onChange={(e) => setVal("cats_title_es", e.target.value)}
                                data-testid="cats-title-es"
                                placeholder="por categoría."
                                className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                            />
                        </label>
                    </div>
                    <div className="space-y-6 pt-2">
                        {[1, 2, 3, 4, 5, 6].map((n) => (
                            <div key={n} className="border border-[#333]/15 p-4 space-y-3">
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5D3FD3]">tile {n}</div>
                                <label className="block">
                                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">image url</span>
                                    <input
                                        value={draft[`cats_tile_${n}_image_url`] || ""}
                                        onChange={(e) => setVal(`cats_tile_${n}_image_url`, e.target.value)}
                                        data-testid={`cats-tile-${n}-image`}
                                        placeholder="https://…"
                                        className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                                    />
                                    {draft[`cats_tile_${n}_image_url`] && (
                                        <img src={draft[`cats_tile_${n}_image_url`]} alt="" className="mt-2 w-40 aspect-[3/4] object-cover border border-[#333]" referrerPolicy="no-referrer" />
                                    )}
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <label className="block">
                                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">label · EN</span>
                                        <input
                                            value={draft[`cats_tile_${n}_label_en`] || ""}
                                            onChange={(e) => setVal(`cats_tile_${n}_label_en`, e.target.value)}
                                            data-testid={`cats-tile-${n}-label-en`}
                                            placeholder="MERCH"
                                            className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">label · ES</span>
                                        <input
                                            value={draft[`cats_tile_${n}_label_es`] || ""}
                                            onChange={(e) => setVal(`cats_tile_${n}_label_es`, e.target.value)}
                                            data-testid={`cats-tile-${n}-label-es`}
                                            placeholder="MERCH"
                                            className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                                        />
                                    </label>
                                </div>
                                <label className="block">
                                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">link</span>
                                    <input
                                        value={draft[`cats_tile_${n}_link`] || ""}
                                        onChange={(e) => setVal(`cats_tile_${n}_link`, e.target.value)}
                                        data-testid={`cats-tile-${n}-link`}
                                        placeholder="/shop  or  https://…"
                                        className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                                    />
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {sub === "hero" && (
                <div className="space-y-6 max-w-3xl">
                    {/* ---- HERO body / description paragraph (left side, under the tagline) ---- */}
                    <div className="space-y-3">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">hero · body paragraph (left side, under tagline)</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                            párrafo descriptivo debajo del tagline &quot;colectivo / label&quot;. para dar contexto de qué es itzkatu. déjalo vacío para no mostrarlo.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">hero body · EN</span>
                                <textarea rows={4} value={draft.hero_body_en || ""} onChange={(e) => setVal("hero_body_en", e.target.value)} data-testid="hero-body-en" placeholder="a collective of djs, producers, and visual artists rooted in san cristobal de las casas." className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">hero body · ES</span>
                                <textarea rows={4} value={draft.hero_body_es || ""} onChange={(e) => setVal("hero_body_es", e.target.value)} data-testid="hero-body-es" placeholder="un colectivo de djs, productores y artistas visuales con base en san cristóbal de las casas." className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                        </div>
                    </div>
                    <div className="border-t border-[#DDD] pt-6"></div>

                    {/* ---- HERO featured artists (highlights) body ---- */}
                    <div className="space-y-3">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">artist highlights · body paragraph</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                            párrafo descriptivo de la sección &quot;en escena&quot; (featured artists) en el home. también editable desde <b>HOME SECTIONS → featured artist</b>.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">highlights body · EN</span>
                                <textarea rows={3} value={draft.body_featured_artist_en || ""} onChange={(e) => setVal("body_featured_artist_en", e.target.value)} data-testid="hero-highlights-body-en" placeholder="artists currently on rotation. click through to their profile." className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">highlights body · ES</span>
                                <textarea rows={3} value={draft.body_featured_artist_es || ""} onChange={(e) => setVal("body_featured_artist_es", e.target.value)} data-testid="hero-highlights-body-es" placeholder="artistas en rotación ahora. click para ver su perfil." className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                        </div>
                    </div>
                    <div className="border-t border-[#DDD] pt-6"></div>

                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">hero — single media block (right side of the hero)</div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                        one big rectangle at the top. can be an image, a spotify playlist / track, a youtube video, a soundcloud embed, or a vimeo url — auto-detected and embedded. optional link, title and subtitle.
                    </p>
                    <label className="block">
                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">media url</span>
                        <input
                            value={draft.hero_media_url || ""}
                            onChange={(e) => setVal("hero_media_url", e.target.value)}
                            data-testid="hero-media-url"
                            placeholder="https://open.spotify.com/playlist/…  or  https://…/image.jpg"
                            className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                        />
                        {draft.hero_media_url && !/spotify|youtube|youtu\.be|soundcloud|vimeo/i.test(draft.hero_media_url) && (
                            <img src={draft.hero_media_url} alt="preview" className="mt-2 w-64 aspect-video object-cover border border-[#333]" referrerPolicy="no-referrer" />
                        )}
                    </label>
                    <label className="block">
                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">link (where clicking goes — ignored for embed media since they capture clicks)</span>
                        <input
                            value={draft.hero_media_link || ""}
                            onChange={(e) => setVal("hero_media_link", e.target.value)}
                            data-testid="hero-media-link"
                            placeholder="/shop  or  https://…"
                            className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                        />
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">title · EN</span>
                            <input
                                value={draft.hero_media_title_en || ""}
                                onChange={(e) => setVal("hero_media_title_en", e.target.value)}
                                data-testid="hero-media-title-en"
                                className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                            />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">title · ES</span>
                            <input
                                value={draft.hero_media_title_es || ""}
                                onChange={(e) => setVal("hero_media_title_es", e.target.value)}
                                data-testid="hero-media-title-es"
                                className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                            />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">subtitle · EN</span>
                            <input
                                value={draft.hero_media_subtitle_en || ""}
                                onChange={(e) => setVal("hero_media_subtitle_en", e.target.value)}
                                data-testid="hero-media-subtitle-en"
                                className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                            />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">subtitle · ES</span>
                            <input
                                value={draft.hero_media_subtitle_es || ""}
                                onChange={(e) => setVal("hero_media_subtitle_es", e.target.value)}
                                data-testid="hero-media-subtitle-es"
                                className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                            />
                        </label>
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                        tip: hero kicker, tagline and CTAs are edited in <b>copy / texts → HOME · hero</b>.
                    </p>
                </div>
            )}

            {sub === "herotiles" && (
                <div className="space-y-6 max-w-4xl">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">4 hero tiles (mosaic on the right of the hero)</div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                        each tile can be an image, or a Spotify / YouTube / SoundCloud / Vimeo url (auto-embedded).
                        set an optional link, title and subtitle. leave media empty to hide the tile.
                    </p>
                    {[1, 2, 3, 4].map((n) => (
                        <div key={n} className="border border-[#333]/20 p-4 space-y-3">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">tile {n}</div>
                            <label className="block">
                                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">media url (image, spotify, youtube, soundcloud, vimeo)</span>
                                <input
                                    value={draft[`hero_tile_${n}_media`] || ""}
                                    onChange={(e) => setVal(`hero_tile_${n}_media`, e.target.value)}
                                    data-testid={`herotile-${n}-media`}
                                    placeholder="https://…"
                                    className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                                />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">link (where clicking the tile goes — optional; ignored for spotify/youtube embeds since they capture clicks)</span>
                                <input
                                    value={draft[`hero_tile_${n}_link`] || ""}
                                    onChange={(e) => setVal(`hero_tile_${n}_link`, e.target.value)}
                                    data-testid={`herotile-${n}-link`}
                                    placeholder="/artists/deuteroz  or  https://…"
                                    className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                                />
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <label className="block">
                                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">title · EN</span>
                                    <input
                                        value={draft[`hero_tile_${n}_title_en`] || ""}
                                        onChange={(e) => setVal(`hero_tile_${n}_title_en`, e.target.value)}
                                        data-testid={`herotile-${n}-title-en`}
                                        className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                                    />
                                </label>
                                <label className="block">
                                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">title · ES</span>
                                    <input
                                        value={draft[`hero_tile_${n}_title_es`] || ""}
                                        onChange={(e) => setVal(`hero_tile_${n}_title_es`, e.target.value)}
                                        data-testid={`herotile-${n}-title-es`}
                                        className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                                    />
                                </label>
                                <label className="block">
                                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">subtitle · EN</span>
                                    <input
                                        value={draft[`hero_tile_${n}_subtitle_en`] || ""}
                                        onChange={(e) => setVal(`hero_tile_${n}_subtitle_en`, e.target.value)}
                                        data-testid={`herotile-${n}-subtitle-en`}
                                        className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                                    />
                                </label>
                                <label className="block">
                                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">subtitle · ES</span>
                                    <input
                                        value={draft[`hero_tile_${n}_subtitle_es`] || ""}
                                        onChange={(e) => setVal(`hero_tile_${n}_subtitle_es`, e.target.value)}
                                        data-testid={`herotile-${n}-subtitle-es`}
                                        className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none"
                                    />
                                </label>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {sub === "statement" && (
                <div className="space-y-6 max-w-4xl">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">member statement (shown at /member/statement)</div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                        markdown supported. use <code>#</code> for the main title, <code>##</code> for sections (they appear in the sidebar TOC), <code>###</code> for sub-blocks, triple backticks for numeric examples, <code>**bold**</code>, <code>- item</code> for lists.
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">EN</span>
                            <textarea
                                value={draft.member_statement_en || ""}
                                onChange={(e) => setVal("member_statement_en", e.target.value)}
                                data-testid="statement-en"
                                rows={30}
                                className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-[11px] leading-relaxed p-3"
                            />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">ES</span>
                            <textarea
                                value={draft.member_statement_es || ""}
                                onChange={(e) => setVal("member_statement_es", e.target.value)}
                                data-testid="statement-es"
                                rows={30}
                                className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-[11px] leading-relaxed p-3"
                            />
                        </label>
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                        tip: preview at <a href="/member/statement" className="underline text-[#5D3FD3]" target="_blank" rel="noreferrer">/member/statement</a> after clicking <b>save all</b>.
                    </p>

                    {/* Invite apply panel copy — everything the invitee sees inside the
                        sticky apply card. Field labels, description, CTA, and per-field
                        visibility toggles all editable here. */}
                    <div className="pt-8 mt-8 border-t border-[#DDD] space-y-5" data-testid="admin-invite-copy">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">invite apply panel</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                            copy shown to invitees on <code>/invite/[token]</code>. leave blank to use defaults.
                        </p>
                        {[
                            ["invite_apply_kicker",  "kicker (¿te interesa? / interested?)"],
                            ["invite_apply_title",   "title (cuéntanos. / tell us.)"],
                            ["invite_apply_desc",    "description (paragraph)"],
                            ["invite_apply_cta",     "submit button label"],
                            ["invite_apply_ok",      "thank-you message"],
                            ["invite_apply_err",     "error message"],
                            ["invite_field_name",    "placeholder — name field"],
                            ["invite_field_email",   "placeholder — email field"],
                            ["invite_field_note",    "placeholder — what-you-love field"],
                            ["invite_field_social",  "placeholder — social field"],
                        ].map(([key, label]) => (
                            <div key={key} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <label className="block">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">{label} · EN</span>
                                    <input
                                        value={draft[`${key}_en`] || ""}
                                        onChange={(e) => setVal(`${key}_en`, e.target.value)}
                                        data-testid={`${key}-en`}
                                        className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                    />
                                </label>
                                <label className="block">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">{label} · ES</span>
                                    <input
                                        value={draft[`${key}_es`] || ""}
                                        onChange={(e) => setVal(`${key}_es`, e.target.value)}
                                        data-testid={`${key}-es`}
                                        className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                    />
                                </label>
                            </div>
                        ))}
                        {/* Field visibility — hide any field the admin doesn't want to collect. */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t border-[#DDD]">
                            {[
                                ["invite_field_show_name",   "show name field"],
                                ["invite_field_show_note",   "show what-you-love field"],
                                ["invite_field_show_social", "show social field"],
                            ].map(([key, label]) => (
                                <label key={key} className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                                    <input
                                        type="checkbox"
                                        checked={draft[key] !== "0"}
                                        onChange={(e) => setVal(key, e.target.checked ? "1" : "0")}
                                        data-testid={key}
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {sub === "shop-front" && (
                <div className="space-y-8 max-w-4xl" data-testid="admin-shop-front-editor">
                    {/* ---- HOME shop section (kicker + title) ---- */}
                    <div className="space-y-3">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">home · shop section title</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                            texto que aparece arriba del grid de productos en la home page. si dejas el título vacío, se oculta (controlado desde &quot;home sections&quot;).
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">kicker · EN</span>
                                <input value={draft.shop_kicker_en || ""} onChange={(e) => setVal("shop_kicker_en", e.target.value)} data-testid="home-shop-kicker-en" placeholder="from the shop" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">kicker · ES</span>
                                <input value={draft.shop_kicker_es || ""} onChange={(e) => setVal("shop_kicker_es", e.target.value)} data-testid="home-shop-kicker-es" placeholder="desde la tienda" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">title · EN</span>
                                <input value={draft.title_shop_en || ""} onChange={(e) => setVal("title_shop_en", e.target.value)} data-testid="home-shop-title-en" placeholder="the store" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">title · ES</span>
                                <input value={draft.title_shop_es || ""} onChange={(e) => setVal("title_shop_es", e.target.value)} data-testid="home-shop-title-es" placeholder="la tienda" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block md:col-span-2">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">body / description text · EN (shown under the title)</span>
                                <textarea rows={3} value={draft.body_shop_en || ""} onChange={(e) => setVal("body_shop_en", e.target.value)} data-testid="home-shop-body-en" placeholder="short description of what's in the shop, drops, etc." className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block md:col-span-2">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">body / description text · ES</span>
                                <textarea rows={3} value={draft.body_shop_es || ""} onChange={(e) => setVal("body_shop_es", e.target.value)} data-testid="home-shop-body-es" placeholder="descripción corta de lo que hay en la tienda, drops, etc." className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            {/* Show / hide toggles for kicker/title/subtitle so admin can quickly show or hide each piece */}
                            <div className="md:col-span-2 grid grid-cols-3 gap-3 pt-2 border-t border-[#DDD]">
                                <label className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                                    <input type="checkbox" checked={draft.home_shop_show_kicker !== "0"} onChange={(e) => setVal("home_shop_show_kicker", e.target.checked ? "1" : "0")} data-testid="home-shop-show-kicker" />
                                    show kicker
                                </label>
                                <label className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                                    <input type="checkbox" checked={draft.home_shop_show_title !== "0"} onChange={(e) => setVal("home_shop_show_title", e.target.checked ? "1" : "0")} data-testid="home-shop-show-title" />
                                    show title
                                </label>
                                <label className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                                    <input type="checkbox" checked={draft.home_shop_show_subtitle !== "0"} onChange={(e) => setVal("home_shop_show_subtitle", e.target.checked ? "1" : "0")} data-testid="home-shop-show-subtitle" />
                                    show subtitle / body
                                </label>
                                <label className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                                    <input type="checkbox" checked={draft.home_shop_effects === "1"} onChange={(e) => setVal("home_shop_effects", e.target.checked ? "1" : "0")} data-testid="home-shop-effects" />
                                    image effects on shop tiles (hover reveals the original)
                                </label>
                            </div>
                            {/* Background image + scheme so the section can be its own "room" */}
                            <label className="block md:col-span-2 pt-2 border-t border-[#DDD]">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">background image URL (optional — makes the shop section its own hero)</span>
                                <input value={draft.home_shop_bg_image_url || ""} onChange={(e) => setVal("home_shop_bg_image_url", e.target.value)} data-testid="home-shop-bg-image" placeholder="https://…" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                                {draft.home_shop_bg_image_url && (
                                    <img src={draft.home_shop_bg_image_url} alt="shop bg preview" className="mt-2 w-full max-h-40 object-cover border border-[#333]" referrerPolicy="no-referrer" />
                                )}
                            </label>
                            <div className="md:col-span-2">
                                <SchemePicker
                                    scheme={draft.home_shop_scheme || ""}
                                    onChange={(v) => setVal("home_shop_scheme", v)}
                                    label="home shop section · color scheme"
                                    testId="home-shop-scheme"
                                />
                            </div>
                            <label className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] md:col-span-2">
                                <input
                                    type="checkbox"
                                    checked={draft.home_shop_show_categories === "1"}
                                    onChange={(e) => setVal("home_shop_show_categories", e.target.checked ? "1" : "0")}
                                    data-testid="home-shop-show-categories"
                                />
                                show category tiles above products (uses the same categories from &quot;tienda (página)&quot; sub-tab)
                            </label>
                        </div>
                    </div>

                    {/* ---- SHOP · product title typography (visible on /shop grid) ---- */}
                    <div className="space-y-3 border-t border-[#DDD] pt-6">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">shop · product title typography</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                            controla el tamaño / peso / fuente del título del producto en la grilla del shop (/shop). el default es una tipografía suave, sin bold.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">size</span>
                                <select
                                    value={draft.shop_title_size || "md"}
                                    onChange={(e) => setVal("shop_title_size", e.target.value)}
                                    data-testid="shop-title-size"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                >
                                    <option value="xs">xs</option>
                                    <option value="sm">sm</option>
                                    <option value="md">md (default)</option>
                                    <option value="lg">lg</option>
                                    <option value="xl">xl</option>
                                </select>
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">weight</span>
                                <select
                                    value={draft.shop_title_weight || "normal"}
                                    onChange={(e) => setVal("shop_title_weight", e.target.value)}
                                    data-testid="shop-title-weight"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                >
                                    <option value="normal">normal (default)</option>
                                    <option value="medium">medium</option>
                                    <option value="bold">bold</option>
                                    <option value="black">black</option>
                                </select>
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">font family</span>
                                <select
                                    value={draft.shop_title_font || "heading"}
                                    onChange={(e) => setVal("shop_title_font", e.target.value)}
                                    data-testid="shop-title-font"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                >
                                    <option value="heading">heading (Space Grotesk)</option>
                                    <option value="body">body (Instrument Serif)</option>
                                    <option value="mono">mono (IBM Plex Mono)</option>
                                </select>
                            </label>
                        </div>
                    </div>

                    {/* ---- SHOP · filters shown on /shop page ---- */}
                    <div className="space-y-3 border-t border-[#DDD] pt-6">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">shop · filters</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                            elegí qué filtros quieres mostrar arriba del grid en la página /shop. sort siempre está visible. si desactivás todo lo demás, la raya divisoria también desaparece.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                                <input
                                    type="checkbox"
                                    checked={draft.shop_show_type_filter !== "0"}
                                    onChange={(e) => setVal("shop_show_type_filter", e.target.checked ? "1" : "0")}
                                    data-testid="shop-show-type-filter"
                                />
                                show &quot;type&quot; filter (apparel, poster, cassette…)
                            </label>
                            <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                                <input
                                    type="checkbox"
                                    checked={draft.shop_show_artist_filter !== "0"}
                                    onChange={(e) => setVal("shop_show_artist_filter", e.target.checked ? "1" : "0")}
                                    data-testid="shop-show-artist-filter"
                                />
                                show &quot;artist&quot; filter (linked artist per product)
                            </label>
                        </div>
                    </div>

                    {/* ---- HOME shop text banner ---- */}
                    <div className="space-y-3 border-t border-[#DDD] pt-6">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">home · shop text banner (optional)</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                            tira de texto (aviso corto) arriba del grid de productos en la home. déjalo vacío para ocultarlo. soporta <b>**negrita**</b> y <b>*itálica*</b>.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">shop text banner · EN</span>
                                <textarea
                                    value={draft.shop_banner_en || ""}
                                    onChange={(e) => setVal("shop_banner_en", e.target.value)}
                                    data-testid="shop-banner-en"
                                    rows={2}
                                    placeholder="e.g. **NEW DROP** — 15% off with code ITZKATU15"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">shop text banner · ES</span>
                                <textarea
                                    value={draft.shop_banner_es || ""}
                                    onChange={(e) => setVal("shop_banner_es", e.target.value)}
                                    data-testid="shop-banner-es"
                                    rows={2}
                                    placeholder="ej. **NUEVO DROP** — 15% off con código ITZKATU15"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                />
                            </label>
                        </div>
                        {/* click-through behaviour */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-[#DDD]">
                            <label className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] md:col-span-3">
                                <input
                                    type="checkbox"
                                    checked={draft.shop_banner_show !== "0"}
                                    onChange={(e) => setVal("shop_banner_show", e.target.checked ? "1" : "0")}
                                    data-testid="shop-banner-show"
                                />
                                show banner on the home shop section (uncheck to hide even if there&apos;s copy)
                            </label>
                            <label className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] md:col-span-3">
                                <input
                                    type="checkbox"
                                    checked={draft.shop_banner_center !== "0"}
                                    onChange={(e) => setVal("shop_banner_center", e.target.checked ? "1" : "0")}
                                    data-testid="shop-banner-center"
                                />
                                center banner text (unchecked = align left)
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">on click</span>
                                <select
                                    value={draft.shop_banner_click_type || "none"}
                                    onChange={(e) => setVal("shop_banner_click_type", e.target.value)}
                                    data-testid="shop-banner-click-type"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                >
                                    <option value="none">nothing (banner is decoration)</option>
                                    <option value="shop">redirect to shop URL</option>
                                    <option value="coupon">open coupon popup with code</option>
                                </select>
                            </label>
                            <label className="block md:col-span-2">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">coupon code (shown in popup, copy button)</span>
                                <input
                                    value={draft.shop_banner_coupon_code || ""}
                                    onChange={(e) => setVal("shop_banner_coupon_code", e.target.value)}
                                    data-testid="shop-banner-coupon-code"
                                    placeholder="ITZKATU15"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                />
                            </label>
                            <label className="block md:col-span-3">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">coupon image URL (shown in popup — optional)</span>
                                <input
                                    value={draft.shop_banner_coupon_image_url || ""}
                                    onChange={(e) => setVal("shop_banner_coupon_image_url", e.target.value)}
                                    data-testid="shop-banner-coupon-image"
                                    placeholder="https://…"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                />
                                {draft.shop_banner_coupon_image_url && (
                                    <img src={draft.shop_banner_coupon_image_url} alt="coupon preview" className="mt-2 w-48 max-h-32 object-contain border border-[#333]" referrerPolicy="no-referrer" />
                                )}
                            </label>
                            <label className="block md:col-span-2">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">banner size</span>
                                <select
                                    value={draft.shop_banner_size || "md"}
                                    onChange={(e) => setVal("shop_banner_size", e.target.value)}
                                    data-testid="shop-banner-size"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                >
                                    <option value="sm">small — subtle strip</option>
                                    <option value="md">medium (default)</option>
                                    <option value="lg">large — big announcement</option>
                                </select>
                            </label>
                        </div>
                    </div>
                </div>
            )}

            {sub === "shop-page" && (
                <div className="space-y-8 max-w-4xl" data-testid="admin-shop-page-editor">
                    {/* Product detail page layout — image % + text alignment + FX rate */}
                    <div className="p-4 border border-[#DDD]">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">product detail page · layout</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] mt-2 leading-relaxed">
                            controla cómo se ve /shop/[producto]: tamaño de la imagen principal, alineación del texto (título/precio/descripción), y el tipo de cambio USD→MXN para el switch de moneda.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">main image size (%)</span>
                                <input
                                    type="number"
                                    min="30"
                                    max="100"
                                    step="5"
                                    value={draft.product_main_image_pct || "50"}
                                    onChange={(e) => setVal("product_main_image_pct", e.target.value)}
                                    data-testid="product-main-image-pct"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">text alignment (right column)</span>
                                <select
                                    value={draft.product_text_align || "left"}
                                    onChange={(e) => setVal("product_text_align", e.target.value)}
                                    data-testid="product-text-align"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                >
                                    <option value="left">left (default)</option>
                                    <option value="center">center</option>
                                    <option value="right">right</option>
                                </select>
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">FX rate · 1 USD in MXN</span>
                                <input
                                    type="number"
                                    min="1"
                                    step="0.1"
                                    value={draft.fx_usd_to_mxn || "20"}
                                    onChange={(e) => setVal("fx_usd_to_mxn", e.target.value)}
                                    data-testid="fx-usd-to-mxn"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                />
                            </label>
                        </div>
                    </div>
                    <div className="p-4 bg-[#0B0B0C] text-white mb-2">
                        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#F0C56C]">promo banner width</div>
                        <div className="mt-2 font-mono text-[10px] text-white/70">
                            controla el ancho del banner promocional (30–100%). se centra automáticamente.
                        </div>
                        <label className="block mt-3">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/70">shop_banner_width_pct — width in %</span>
                            <input
                                type="number"
                                min="30"
                                max="100"
                                step="5"
                                value={draft.shop_banner_width_pct || "60"}
                                onChange={(e) => setVal("shop_banner_width_pct", e.target.value)}
                                data-testid="shop-banner-width-pct"
                                className="mt-1 w-full bg-transparent border border-white/40 font-mono text-xs p-2 focus:border-[#F0C56C] outline-none text-white"
                            />
                        </label>
                    </div>
                    <SchemePicker
                        scheme={draft.shop_page_banner_scheme || ""}
                        onChange={(v) => setVal("shop_page_banner_scheme", v)}
                        label="shop promo banner — color scheme (bg + text)"
                        testId="shop-banner-scheme"
                    />
                    {/* ---- SHOP PAGE (/shop) main heading ---- */}
                    <div className="space-y-3">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">shop page · main heading (/shop)</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                            edita el encabezado, subtítulo, intro y el CTA de la subpágina <b>/shop</b>. si dejas alguno vacío, se usa el default en el idioma correspondiente.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">page kicker (small label) · EN</span>
                                <input value={draft.shop_page_kicker_en || ""} onChange={(e) => setVal("shop_page_kicker_en", e.target.value)} data-testid="shop-page-kicker-en" placeholder="shop" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">page kicker · ES</span>
                                <input value={draft.shop_page_kicker_es || ""} onChange={(e) => setVal("shop_page_kicker_es", e.target.value)} data-testid="shop-page-kicker-es" placeholder="tienda" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">big title (H1) · EN</span>
                                <input value={draft.shop_page_title_en || ""} onChange={(e) => setVal("shop_page_title_en", e.target.value)} data-testid="shop-page-title-en" placeholder="shop" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">big title (H1) · ES</span>
                                <input value={draft.shop_page_title_es || ""} onChange={(e) => setVal("shop_page_title_es", e.target.value)} data-testid="shop-page-title-es" placeholder="tienda" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block md:col-span-2">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">intro paragraph · EN</span>
                                <textarea rows={3} value={draft.shop_page_intro_en || ""} onChange={(e) => setVal("shop_page_intro_en", e.target.value)} data-testid="shop-page-intro-en" placeholder="wearable art, records, printed matter — everything is made / curated by artists in the collective." className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block md:col-span-2">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">intro paragraph · ES</span>
                                <textarea rows={3} value={draft.shop_page_intro_es || ""} onChange={(e) => setVal("shop_page_intro_es", e.target.value)} data-testid="shop-page-intro-es" placeholder="arte usable, discos, publicaciones — todo hecho / curado por artistas del colectivo." className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">CTA button label · EN</span>
                                <input value={draft.shop_page_cta_en || ""} onChange={(e) => setVal("shop_page_cta_en", e.target.value)} data-testid="shop-page-cta-en" placeholder="visit the full store" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">CTA button label · ES</span>
                                <input value={draft.shop_page_cta_es || ""} onChange={(e) => setVal("shop_page_cta_es", e.target.value)} data-testid="shop-page-cta-es" placeholder="visitar la tienda completa" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block md:col-span-2">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">CTA link (external store URL — used by the button above and the header SHOP button)</span>
                                <input value={draft.store_external_url || ""} onChange={(e) => setVal("store_external_url", e.target.value)} data-testid="shop-page-external-url" placeholder="https://itzkatu.etsy.com" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                        </div>
                    </div>

                    {/* ---- SHOP PAGE image banners (up to 3) ---- */}
                    <div className="space-y-3 border-t border-[#DDD] pt-6">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">shop page · image banners (up to 3)</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                            big image banners at the top of <b>/shop</b>. leave a slot empty to skip it. auto-hides if all empty.
                        </p>
                        {[1, 2, 3].map((n) => (
                            <div key={n} className="border border-[#DDD] p-4 space-y-3">
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#333]">banner {n}</div>
                                <label className="block">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">image url</span>
                                    <input
                                        value={draft[`shop_page_banner_${n}_image_url`] || ""}
                                        onChange={(e) => setVal(`shop_page_banner_${n}_image_url`, e.target.value)}
                                        data-testid={`shop-page-banner-${n}-image`}
                                        placeholder="https://…"
                                        className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                    />
                                    {draft[`shop_page_banner_${n}_image_url`] && (
                                        <img src={draft[`shop_page_banner_${n}_image_url`]} alt="preview" className="mt-2 w-64 aspect-[16/9] object-cover border border-[#333]" referrerPolicy="no-referrer" />
                                    )}
                                </label>
                                <label className="block">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">click link (external URL or /path). optional.</span>
                                    <input
                                        value={draft[`shop_page_banner_${n}_link`] || ""}
                                        onChange={(e) => setVal(`shop_page_banner_${n}_link`, e.target.value)}
                                        data-testid={`shop-page-banner-${n}-link`}
                                        placeholder="https://itzkatu.etsy.com/… or /events"
                                        className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                    />
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">overlay label · EN</span>
                                        <input
                                            value={draft[`shop_page_banner_${n}_label_en`] || ""}
                                            onChange={(e) => setVal(`shop_page_banner_${n}_label_en`, e.target.value)}
                                            data-testid={`shop-page-banner-${n}-label-en`}
                                            placeholder="new drop · fall 26"
                                            className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">overlay label · ES</span>
                                        <input
                                            value={draft[`shop_page_banner_${n}_label_es`] || ""}
                                            onChange={(e) => setVal(`shop_page_banner_${n}_label_es`, e.target.value)}
                                            data-testid={`shop-page-banner-${n}-label-es`}
                                            placeholder="nuevo drop · otoño 26"
                                            className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                        />
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ---- SHOP PAGE categories (up to 6) ---- */}
                    <div className="space-y-3 border-t border-[#DDD] pt-6">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">shop page · categories (up to 6)</div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                            square category tiles shown before the products on <b>/shop</b>. click filters by <code>product_type</code>. use exact match — e.g. tile filter <code>hoodie</code> matches products with type <code>hoodie</code>. leave filter as <code>all</code> to show everything.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">categories kicker · EN</span>
                                <input
                                    value={draft.shop_categories_kicker_en || ""}
                                    onChange={(e) => setVal("shop_categories_kicker_en", e.target.value)}
                                    placeholder="shop by category"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">categories kicker · ES</span>
                                <input
                                    value={draft.shop_categories_kicker_es || ""}
                                    onChange={(e) => setVal("shop_categories_kicker_es", e.target.value)}
                                    placeholder="categorías"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                />
                            </label>
                        </div>
                        {[1, 2, 3, 4, 5, 6].map((n) => (
                            <div key={n} className="border border-[#DDD] p-4 space-y-3">
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#333]">category {n}</div>
                                <label className="block">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">image url</span>
                                    <input
                                        value={draft[`shop_category_${n}_image_url`] || ""}
                                        onChange={(e) => setVal(`shop_category_${n}_image_url`, e.target.value)}
                                        data-testid={`shop-category-${n}-image`}
                                        placeholder="https://…"
                                        className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                    />
                                    {draft[`shop_category_${n}_image_url`] && (
                                        <img src={draft[`shop_category_${n}_image_url`]} alt="preview" className="mt-2 w-32 h-32 object-cover border border-[#333]" referrerPolicy="no-referrer" />
                                    )}
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">label · EN</span>
                                        <input
                                            value={draft[`shop_category_${n}_label_en`] || ""}
                                            onChange={(e) => setVal(`shop_category_${n}_label_en`, e.target.value)}
                                            data-testid={`shop-category-${n}-label-en`}
                                            placeholder="new"
                                            className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">label · ES</span>
                                        <input
                                            value={draft[`shop_category_${n}_label_es`] || ""}
                                            onChange={(e) => setVal(`shop_category_${n}_label_es`, e.target.value)}
                                            data-testid={`shop-category-${n}-label-es`}
                                            placeholder="nuevo"
                                            className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">filter (product_type or &quot;all&quot;)</span>
                                        <input
                                            value={draft[`shop_category_${n}_filter`] || ""}
                                            onChange={(e) => setVal(`shop_category_${n}_filter`, e.target.value)}
                                            data-testid={`shop-category-${n}-filter`}
                                            placeholder="all / hoodie / vinyl / poster"
                                            className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                        />
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {sub === "media" && (
                <div className="space-y-6 max-w-3xl" data-testid="admin-media-editor">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">night vibes / vibras nocturnas</div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                        the public section title. curation lives in the <b>scene</b> tab — each scene feature with an <code>embed_url</code> automatically shows up here (up to 4 on the homepage). items without an embed stay reserved for artist pages and collections.
                    </p>
                    {/* Visibility toggles for the header block — kicker/title/subtitle/text
                        default to OFF ("0") so admins consciously turn them on and see them appear. */}
                    <div className="flex flex-wrap gap-4 border border-[#DDD] p-3 rounded">
                        <label className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em]">
                            <input type="checkbox" checked={draft.night_vibes_show_kicker === "1"} onChange={(e) => setVal("night_vibes_show_kicker", e.target.checked ? "1" : "0")} data-testid="night-vibes-show-kicker" />
                            show kicker
                        </label>
                        <label className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em]">
                            <input type="checkbox" checked={draft.night_vibes_show_title === "1"} onChange={(e) => setVal("night_vibes_show_title", e.target.checked ? "1" : "0")} data-testid="night-vibes-show-title" />
                            show title
                        </label>
                        <label className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em]">
                            <input type="checkbox" checked={draft.night_vibes_show_subtitle === "1"} onChange={(e) => setVal("night_vibes_show_subtitle", e.target.checked ? "1" : "0")} data-testid="night-vibes-show-subtitle" />
                            show subtitle
                        </label>
                        <label className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em]">
                            <input type="checkbox" checked={draft.night_vibes_show_text === "1"} onChange={(e) => setVal("night_vibes_show_text", e.target.checked ? "1" : "0")} data-testid="night-vibes-show-text" />
                            show text block
                        </label>
                    </div>
                    {/* Layout presets — columns + container width. Purely visual, no data change. */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-[#DDD] p-3 rounded">
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">columns (desktop)</span>
                            <select
                                value={draft.scene_columns_home || draft.night_vibes_columns || "4"}
                                onChange={(e) => setVal("scene_columns_home", e.target.value)}
                                data-testid="scene-columns"
                                className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                            >
                                <option value="2">2 columns — tight editorial</option>
                                <option value="3">3 columns — balanced</option>
                                <option value="4">4 columns — dense grid (default)</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">container width</span>
                            <select
                                value={draft.scene_container_home || draft.night_vibes_container || "default"}
                                onChange={(e) => setVal("scene_container_home", e.target.value)}
                                data-testid="scene-container"
                                className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                            >
                                <option value="narrow">narrow (max-w-3xl)</option>
                                <option value="default">default (max-w-6xl)</option>
                                <option value="wide">wide (max-w-7xl)</option>
                                <option value="full">full-bleed (no max width)</option>
                            </select>
                        </label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">section kicker · EN</span>
                            <input
                                value={draft.night_vibes_kicker_en || ""}
                                onChange={(e) => setVal("night_vibes_kicker_en", e.target.value)}
                                data-testid="night-vibes-kicker-en"
                                placeholder="night vibes"
                                className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                            />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">section kicker · ES</span>
                            <input
                                value={draft.night_vibes_kicker_es || ""}
                                onChange={(e) => setVal("night_vibes_kicker_es", e.target.value)}
                                data-testid="night-vibes-kicker-es"
                                placeholder="vibras nocturnas"
                                className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                            />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">section title · EN</span>
                            <input
                                value={draft.night_vibes_title_en || ""}
                                onChange={(e) => setVal("night_vibes_title_en", e.target.value)}
                                data-testid="night-vibes-title-en"
                                placeholder="Night Vibes"
                                className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                            />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">section title · ES</span>
                            <input
                                value={draft.night_vibes_title_es || ""}
                                onChange={(e) => setVal("night_vibes_title_es", e.target.value)}
                                data-testid="night-vibes-title-es"
                                placeholder="Vibras Nocturnas"
                                className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                            />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">subtitle · EN (optional, short)</span>
                            <input
                                value={draft.night_vibes_subtitle_en || ""}
                                onChange={(e) => setVal("night_vibes_subtitle_en", e.target.value)}
                                placeholder=""
                                className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                            />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">subtitle · ES (opcional, corto)</span>
                            <input
                                value={draft.night_vibes_subtitle_es || ""}
                                onChange={(e) => setVal("night_vibes_subtitle_es", e.target.value)}
                                placeholder=""
                                className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                            />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">text block · EN (optional, long — supports line breaks)</span>
                            <textarea
                                rows={4}
                                value={draft.night_vibes_text_en || ""}
                                onChange={(e) => setVal("night_vibes_text_en", e.target.value)}
                                data-testid="night-vibes-text-en"
                                placeholder=""
                                className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                            />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">text block · ES (opcional, largo — acepta saltos de línea)</span>
                            <textarea
                                rows={4}
                                value={draft.night_vibes_text_es || ""}
                                onChange={(e) => setVal("night_vibes_text_es", e.target.value)}
                                data-testid="night-vibes-text-es"
                                placeholder=""
                                className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                            />
                        </label>
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed border-t border-[#DDD] pt-4">
                        tip: to hide this section entirely, go to <b>home sections</b> and uncheck <b>media player</b>. to curate what shows up, use the <b>scene</b> tab — only features with an embed url are surfaced here.
                    </p>

                    {/* Signals counter — text + on/off */}
                    <div className="border-t border-[#DDD] pt-4 space-y-3">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#333]">signals counter (top-right of section)</div>
                        <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                            <input type="checkbox" checked={draft.night_vibes_show_signals !== "0"} onChange={(e) => setVal("night_vibes_show_signals", e.target.checked ? "1" : "0")} data-testid="night-vibes-show-signals" />
                            show &quot;N signals&quot; counter
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">signals label · EN</span>
                                <input value={draft.night_vibes_signals_label_en || ""} onChange={(e) => setVal("night_vibes_signals_label_en", e.target.value)} placeholder="signals" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">signals label · ES</span>
                                <input value={draft.night_vibes_signals_label_es || ""} onChange={(e) => setVal("night_vibes_signals_label_es", e.target.value)} placeholder="señales" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                        </div>
                    </div>

                    {/* Home CTAs — editable button labels */}
                    <div className="border-t border-[#DDD] pt-4 space-y-3">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#333]">home CTAs (button texts)</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">full roster CTA · EN</span>
                                <input value={draft.home_full_roster_cta_en || ""} onChange={(e) => setVal("home_full_roster_cta_en", e.target.value)} placeholder="full roster →" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">full roster CTA · ES</span>
                                <input value={draft.home_full_roster_cta_es || ""} onChange={(e) => setVal("home_full_roster_cta_es", e.target.value)} placeholder="ver roster completo →" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                        </div>
                    </div>

                    {/* Shop CTA labels + banner intro copy */}
                    <div className="border-t border-[#DDD] pt-4 space-y-3">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#333]">shop buttons + banner intro</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">quick view label · EN</span>
                                <input value={draft.shop_quick_view_label_en || ""} onChange={(e) => setVal("shop_quick_view_label_en", e.target.value)} placeholder="quick view" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">quick view label · ES</span>
                                <input value={draft.shop_quick_view_label_es || ""} onChange={(e) => setVal("shop_quick_view_label_es", e.target.value)} placeholder="vista rápida" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">buy-on-etsy label · EN</span>
                                <input value={draft.shop_buy_etsy_label_en || ""} onChange={(e) => setVal("shop_buy_etsy_label_en", e.target.value)} placeholder="buy on etsy" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">buy-on-etsy label · ES</span>
                                <input value={draft.shop_buy_etsy_label_es || ""} onChange={(e) => setVal("shop_buy_etsy_label_es", e.target.value)} placeholder="comprar en etsy" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                        </div>
                        <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] pt-2">
                            <input type="checkbox" checked={draft.shop_banners_intro_show === "1"} onChange={(e) => setVal("shop_banners_intro_show", e.target.checked ? "1" : "0")} data-testid="shop-banners-intro-show" />
                            show text above shop banners
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">banners intro · EN</span>
                                <input value={draft.shop_banners_intro_en || ""} onChange={(e) => setVal("shop_banners_intro_en", e.target.value)} placeholder="new drops · fall 26" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">banners intro · ES</span>
                                <input value={draft.shop_banners_intro_es || ""} onChange={(e) => setVal("shop_banners_intro_es", e.target.value)} placeholder="nuevos drops · otoño 26" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                        </div>
                    </div>
                </div>
            )}


            {sub === "pages" && (
                <div className="space-y-6 max-w-3xl" data-testid="admin-pages-editor">
                    <div className="p-4 bg-[#0B0B0C] text-white">
                        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#F0C56C]">per-page theme</div>
                        <div className="mt-2 font-mono text-xs text-white/70">
                            each page picks its own color scheme — think of them as different rooms in the same world.
                            defaults: home=cream · artists=white · shop=charcoal · after-dark=black · contact=white · studio=cream · events=sand.
                        </div>
                    </div>
                    {[
                        ["home", "home (/)"],
                        ["artists", "artists (/artists)"],
                        ["shop", "shop (/shop)"],
                        ["after-dark", "after dark (/after-dark)"],
                        ["contact", "contact (/contact)"],
                        ["studio", "studio (/studio)"],
                        ["events", "events (/events)"],
                        ["lab", "lab (/lab)"],
                    ].map(([slug, label]) => (
                        <div key={slug} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#111]">{label}</div>
                            <select
                                value={draft[`page_scheme_${slug}`] || ""}
                                onChange={(e) => setVal(`page_scheme_${slug}`, e.target.value)}
                                data-testid={`page-scheme-${slug}`}
                                className="md:col-span-2 w-full bg-transparent border border-[#333] font-mono text-sm p-2 focus:border-[#5D3FD3] outline-none"
                            >
                                {SCHEME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                    ))}
                </div>
            )}

            {sub === "artists-page" && (
                <div className="space-y-6 max-w-3xl" data-testid="admin-artists-page-editor">
                    <div className="p-4 bg-[#0B0B0C] text-white">
                        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#F0C56C]">artists page</div>
                        <div className="mt-2 font-mono text-xs text-white/70">
                            controls the PAGE at /artists — visibility of kicker/title/intro + the copy itself.
                            (individual artists are edited in the <b>artists</b> tab above.)
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                            <input type="checkbox" checked={draft.artists_page_show_kicker !== "0"} onChange={(e) => setVal("artists_page_show_kicker", e.target.checked ? "1" : "0")} data-testid="artists-page-show-kicker" />
                            show kicker
                        </label>
                        <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                            <input type="checkbox" checked={draft.artists_page_show_title !== "0"} onChange={(e) => setVal("artists_page_show_title", e.target.checked ? "1" : "0")} data-testid="artists-page-show-title" />
                            show title
                        </label>
                        <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                            <input type="checkbox" checked={draft.artists_page_show_intro !== "0"} onChange={(e) => setVal("artists_page_show_intro", e.target.checked ? "1" : "0")} data-testid="artists-page-show-intro" />
                            show intro paragraph
                        </label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">kicker · EN</span>
                            <input value={draft.artists_page_kicker_en || ""} onChange={(e) => setVal("artists_page_kicker_en", e.target.value)} className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">kicker · ES</span>
                            <input value={draft.artists_page_kicker_es || ""} onChange={(e) => setVal("artists_page_kicker_es", e.target.value)} className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">title · EN</span>
                            <input value={draft.artists_page_title_en || ""} onChange={(e) => setVal("artists_page_title_en", e.target.value)} className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">title · ES</span>
                            <input value={draft.artists_page_title_es || ""} onChange={(e) => setVal("artists_page_title_es", e.target.value)} className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">intro paragraph · EN</span>
                            <textarea rows={3} value={draft.artists_page_intro_en || ""} onChange={(e) => setVal("artists_page_intro_en", e.target.value)} className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">intro paragraph · ES</span>
                            <textarea rows={3} value={draft.artists_page_intro_es || ""} onChange={(e) => setVal("artists_page_intro_es", e.target.value)} className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                        </label>
                    </div>
                    {/* Artist tile display — controls what appears under each artist's image
                        on both /artists and the home roster (discipline, city). */}
                    <div className="mt-6 pt-4 border-t border-[#DDD]">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-3">
                            artist tile · display
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                                <input
                                    type="checkbox"
                                    checked={draft.artist_tile_show_discipline !== "0"}
                                    onChange={(e) => setVal("artist_tile_show_discipline", e.target.checked ? "1" : "0")}
                                    data-testid="artist-tile-show-discipline"
                                />
                                show discipline (dj, producer, …)
                            </label>
                            <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                                <input
                                    type="checkbox"
                                    checked={draft.artist_tile_show_location !== "0"}
                                    onChange={(e) => setVal("artist_tile_show_location", e.target.checked ? "1" : "0")}
                                    data-testid="artist-tile-show-location"
                                />
                                show city / location
                            </label>
                        </div>
                    </div>

                    {/* Artist DETAIL header meta (discipline / location) — independent
                        from the tile toggles above. */}
                    <div className="mt-6 pt-4 border-t border-[#DDD]">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-3">
                            artist detail page · header meta
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                                <input
                                    type="checkbox"
                                    checked={draft.artist_detail_show_discipline !== "0"}
                                    onChange={(e) => setVal("artist_detail_show_discipline", e.target.checked ? "1" : "0")}
                                    data-testid="artist-detail-show-discipline"
                                />
                                show discipline (italic)
                            </label>
                            <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                                <input
                                    type="checkbox"
                                    checked={draft.artist_detail_show_location !== "0"}
                                    onChange={(e) => setVal("artist_detail_show_location", e.target.checked ? "1" : "0")}
                                    data-testid="artist-detail-show-location"
                                />
                                show city / location
                            </label>
                        </div>
                    </div>

                    {/* Home roster · "full roster →" CTA text (per language) */}
                    <div className="mt-6 pt-4 border-t border-[#DDD]">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-3">
                            home roster · &quot;full roster&quot; link text
                        </div>
                        <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] mb-3">
                            <input
                                type="checkbox"
                                checked={draft.home_show_full_roster_cta !== "0"}
                                onChange={(e) => setVal("home_show_full_roster_cta", e.target.checked ? "1" : "0")}
                                data-testid="home-show-full-roster-cta"
                            />
                            show link on home
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">EN</span>
                                <input
                                    value={draft.home_full_roster_cta_en || ""}
                                    onChange={(e) => setVal("home_full_roster_cta_en", e.target.value)}
                                    data-testid="home-full-roster-cta-en"
                                    placeholder="full roster →"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">ES</span>
                                <input
                                    value={draft.home_full_roster_cta_es || ""}
                                    onChange={(e) => setVal("home_full_roster_cta_es", e.target.value)}
                                    data-testid="home-full-roster-cta-es"
                                    placeholder="ver roster completo →"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                />
                            </label>
                        </div>
                    </div>

                    {/* Home shop · "explore the shop" CTA text (per language) */}
                    <div className="mt-6 pt-4 border-t border-[#DDD]">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-3">
                            home shop · &quot;explore the shop&quot; link text
                        </div>
                        <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] mb-3">
                            <input
                                type="checkbox"
                                checked={draft.home_show_explore_shop_cta !== "0"}
                                onChange={(e) => setVal("home_show_explore_shop_cta", e.target.checked ? "1" : "0")}
                                data-testid="home-show-explore-shop-cta"
                            />
                            show link on home
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">EN</span>
                                <input
                                    value={draft.home_full_shop_cta_en || ""}
                                    onChange={(e) => setVal("home_full_shop_cta_en", e.target.value)}
                                    data-testid="home-full-shop-cta-en"
                                    placeholder="explore the shop"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">ES</span>
                                <input
                                    value={draft.home_full_shop_cta_es || ""}
                                    onChange={(e) => setVal("home_full_shop_cta_es", e.target.value)}
                                    data-testid="home-full-shop-cta-es"
                                    placeholder="ver toda la tienda"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                />
                            </label>
                        </div>
                    </div>
                </div>
            )}

            {sub === "events-page" && (
                <div className="space-y-6 max-w-3xl" data-testid="admin-events-page-editor">
                    <div className="p-4 bg-[#0B0B0C] text-white">
                        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#F0C56C]">events page</div>
                        <div className="mt-2 font-mono text-xs text-white/70">
                            controls the PAGE at /events — kicker/title/intro + the &quot;upcoming&quot; and &quot;past&quot; labels.
                            (individual events are edited in the <b>events</b> tab above. the HOME events section title/copy
                            is edited in <b>home sections</b>.)
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                            <input type="checkbox" checked={draft.events_page_show_kicker !== "0"} onChange={(e) => setVal("events_page_show_kicker", e.target.checked ? "1" : "0")} data-testid="events-page-show-kicker" />
                            show kicker
                        </label>
                        <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                            <input type="checkbox" checked={draft.events_page_show_title !== "0"} onChange={(e) => setVal("events_page_show_title", e.target.checked ? "1" : "0")} data-testid="events-page-show-title" />
                            show title
                        </label>
                        <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]">
                            <input type="checkbox" checked={draft.events_page_show_intro !== "0"} onChange={(e) => setVal("events_page_show_intro", e.target.checked ? "1" : "0")} data-testid="events-page-show-intro" />
                            show intro paragraph
                        </label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">kicker · EN</span>
                            <input value={draft.events_page_kicker_en || ""} onChange={(e) => setVal("events_page_kicker_en", e.target.value)} data-testid="events-page-kicker-en" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">kicker · ES</span>
                            <input value={draft.events_page_kicker_es || ""} onChange={(e) => setVal("events_page_kicker_es", e.target.value)} data-testid="events-page-kicker-es" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">title · EN</span>
                            <input value={draft.events_page_title_en || ""} onChange={(e) => setVal("events_page_title_en", e.target.value)} data-testid="events-page-title-en" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">title · ES</span>
                            <input value={draft.events_page_title_es || ""} onChange={(e) => setVal("events_page_title_es", e.target.value)} data-testid="events-page-title-es" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">intro paragraph · EN</span>
                            <textarea rows={3} value={draft.events_page_intro_en || ""} onChange={(e) => setVal("events_page_intro_en", e.target.value)} data-testid="events-page-intro-en" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">intro paragraph · ES</span>
                            <textarea rows={3} value={draft.events_page_intro_es || ""} onChange={(e) => setVal("events_page_intro_es", e.target.value)} data-testid="events-page-intro-es" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                        </label>
                    </div>
                    <div className="mt-6 pt-4 border-t border-[#DDD]">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-3">section labels</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">&quot;upcoming&quot; label · EN</span>
                                <input value={draft.events_upcoming_label_en || ""} onChange={(e) => setVal("events_upcoming_label_en", e.target.value)} placeholder="upcoming" data-testid="events-upcoming-label-en" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">&quot;próximos&quot; label · ES</span>
                                <input value={draft.events_upcoming_label_es || ""} onChange={(e) => setVal("events_upcoming_label_es", e.target.value)} placeholder="próximos" data-testid="events-upcoming-label-es" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">&quot;past&quot; label · EN</span>
                                <input value={draft.events_past_label_en || ""} onChange={(e) => setVal("events_past_label_en", e.target.value)} placeholder="past" data-testid="events-past-label-en" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">&quot;pasados&quot; label · ES</span>
                                <input value={draft.events_past_label_es || ""} onChange={(e) => setVal("events_past_label_es", e.target.value)} placeholder="pasados" data-testid="events-past-label-es" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                        </div>
                    </div>
                </div>
            )}


            {sub === "after-dark" && (
                <div className="space-y-6 max-w-3xl" data-testid="admin-after-dark-editor">
                    <div className="p-4 bg-[#0B0B0C] text-white">
                        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#F0C56C]">after dark · canal oculto</div>
                        <div className="mt-2 font-mono text-xs text-white/70">
                            página pública fija: <b>/after-dark</b> · el QR impreso apunta siempre acá — cambiás el contenido cuando quieras.
                            los items se crean/editan en la pestaña principal <b>after-dark</b> (arriba, junto a artists/scene/etc). acá configurás textos + cupón activo.
                        </div>
                    </div>

                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-3">intro / cabecera</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">kicker · EN</span>
                                <input value={draft.after_dark_kicker_en || ""} onChange={(e) => setVal("after_dark_kicker_en", e.target.value)} placeholder="hidden channel" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">kicker · ES</span>
                                <input value={draft.after_dark_kicker_es || ""} onChange={(e) => setVal("after_dark_kicker_es", e.target.value)} placeholder="canal oculto" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">título · EN</span>
                                <input value={draft.after_dark_title_en || ""} onChange={(e) => setVal("after_dark_title_en", e.target.value)} placeholder="After Dark" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">título · ES</span>
                                <input value={draft.after_dark_title_es || ""} onChange={(e) => setVal("after_dark_title_es", e.target.value)} placeholder="After Dark" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block md:col-span-2">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">subtítulo · EN (una línea)</span>
                                <input value={draft.after_dark_subtitle_en || ""} onChange={(e) => setVal("after_dark_subtitle_en", e.target.value)} className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block md:col-span-2">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">subtítulo · ES (una línea)</span>
                                <input value={draft.after_dark_subtitle_es || ""} onChange={(e) => setVal("after_dark_subtitle_es", e.target.value)} className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block md:col-span-2">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">párrafo intro · EN</span>
                                <textarea rows={3} value={draft.after_dark_intro_en || ""} onChange={(e) => setVal("after_dark_intro_en", e.target.value)} className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block md:col-span-2">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">párrafo intro · ES</span>
                                <textarea rows={3} value={draft.after_dark_intro_es || ""} onChange={(e) => setVal("after_dark_intro_es", e.target.value)} className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                        </div>
                        {/* Layout presets — mirrors Scene section so the mental model matches. */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-[#DDD] pt-4">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">columns (desktop)</span>
                                <select
                                    value={draft.after_dark_columns || "2"}
                                    onChange={(e) => setVal("after_dark_columns", e.target.value)}
                                    data-testid="after-dark-columns"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                >
                                    <option value="1">1 column — long-form editorial</option>
                                    <option value="2">2 columns — balanced (default)</option>
                                    <option value="3">3 columns — dense archive</option>
                                </select>
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">container width</span>
                                <select
                                    value={draft.after_dark_container || "default"}
                                    onChange={(e) => setVal("after_dark_container", e.target.value)}
                                    data-testid="after-dark-container"
                                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                                >
                                    <option value="narrow">narrow (max-w-3xl)</option>
                                    <option value="default">default (max-w-5xl)</option>
                                    <option value="wide">wide (max-w-6xl)</option>
                                    <option value="full">full-bleed (no max width)</option>
                                </select>
                            </label>
                        </div>
                    </div>

                    <div className="border-t border-[#DDD] pt-4">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-3">cupón activo (opcional · uno a la vez)</div>
                        <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] mb-3">
                            <input type="checkbox" checked={draft.ad_coupon_active === "1" || draft.ad_coupon_active === true} onChange={(e) => setVal("ad_coupon_active", e.target.checked ? "1" : "0")} data-testid="ad-coupon-active" />
                            mostrar cupón en /after-dark
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">código (click-to-copy)</span>
                                <input value={draft.ad_coupon_code || ""} onChange={(e) => setVal("ad_coupon_code", e.target.value)} placeholder="NIGHT-10" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">expira (opcional · si vacío, no expira)</span>
                                <input type="datetime-local" value={(draft.ad_coupon_expires_at || "").slice(0, 16)} onChange={(e) => setVal("ad_coupon_expires_at", e.target.value ? new Date(e.target.value).toISOString() : "")} className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">título · EN</span>
                                <input value={draft.ad_coupon_label_en || ""} onChange={(e) => setVal("ad_coupon_label_en", e.target.value)} placeholder="a small gift" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">título · ES</span>
                                <input value={draft.ad_coupon_label_es || ""} onChange={(e) => setVal("ad_coupon_label_es", e.target.value)} placeholder="un pequeño regalo" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block md:col-span-2">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">descripción · EN</span>
                                <input value={draft.ad_coupon_description_en || ""} onChange={(e) => setVal("ad_coupon_description_en", e.target.value)} className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block md:col-span-2">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">descripción · ES</span>
                                <input value={draft.ad_coupon_description_es || ""} onChange={(e) => setVal("ad_coupon_description_es", e.target.value)} className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                            <label className="block md:col-span-2">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">botón &quot;usar&quot; · url (opcional)</span>
                                <input value={draft.ad_coupon_url || ""} onChange={(e) => setVal("ad_coupon_url", e.target.value)} placeholder="https://itzkatu.etsy.com/…" className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2" />
                            </label>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}


// -----------------------------------------------------------------------------
// INVITES EDITOR — admin creates personalized invite links for prospective members.
// Each has a unique token; opens + email submission are tracked per link.
// -----------------------------------------------------------------------------
function InvitesEditor() {
    const [items, setItems] = useState([]);
    const [name, setName] = useState("");
    const [note, setNote] = useState("");
    const [personalMessage, setPersonalMessage] = useState("");
    const [creating, setCreating] = useState(false);
    const [editingToken, setEditingToken] = useState(null);
    const [editDraft, setEditDraft] = useState({ name: "", note: "", personal_message: "" });
    const base = window.location.origin;

    const load = async () => {
        try { const r = await api.get("/invites"); setItems(r.data); } catch (_) { /* noop */ }
    };
    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            await api.post("/invites", {
                name: name.trim(),
                note: note.trim(),
                personal_message: personalMessage.trim(),
            });
            setName(""); setNote(""); setPersonalMessage("");
            toast.success("invite created");
            await load();
        } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "error"); }
        setCreating(false);
    };

    const remove = async (token) => {
        if (!window.confirm("delete invite?")) return;
        try { await api.delete(`/invites/${token}`); await load(); } catch (_) { /* noop */ }
    };

    const startEdit = (it) => {
        setEditingToken(it.token);
        setEditDraft({
            name: it.name || "",
            note: it.note || "",
            personal_message: it.personal_message || "",
        });
    };

    const saveEdit = async () => {
        try {
            await api.put(`/invites/${editingToken}`, editDraft);
            toast.success("updated");
            setEditingToken(null);
            await load();
        } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "error"); }
    };

    const copy = (url) => { navigator.clipboard.writeText(url); toast.success("link copied"); };

    return (
        <div data-testid="admin-invites" className="space-y-6 max-w-5xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">invites</div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                see when they open it, how much they read, and if they left their email.
            </p>
            <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end border border-[#333]/15 p-4">
                <label className="block">
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">name (private label)</span>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Diana" data-testid="invite-new-name" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                </label>
                <label className="block">
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">note (internal only)</span>
                    <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="conocida en area 51" data-testid="invite-new-note" className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                </label>
                <button type="submit" disabled={creating} data-testid="invite-create" className="bg-black text-white hover:bg-[#5D3FD3] transition-colors px-6 py-3 font-mono text-[10px] uppercase tracking-[0.3em]">
                    {creating ? "…" : "create link"}
                </button>
                <label className="block md:col-span-3">
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">personal message (shown to the invitee at the top of their invite page)</span>
                    <textarea rows={3} value={personalMessage} onChange={(e) => setPersonalMessage(e.target.value)} data-testid="invite-new-personal-message" placeholder="diana — te vi haciendo el mural en area 51 y quiero que seas parte de esto." className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none resize-y" />
                </label>
            </form>
            <div className="border border-[#333]/15">
                <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#333]/15 font-mono text-[9px] uppercase tracking-[0.25em] text-[#555]">
                    <div className="col-span-2">name</div>
                    <div className="col-span-4">link</div>
                    <div className="col-span-1 text-center">opens</div>
                    <div className="col-span-2 text-center">scroll %</div>
                    <div className="col-span-2 text-center">submitted</div>
                    <div className="col-span-1"></div>
                </div>
                {items.length === 0 && (
                    <div className="px-4 py-6 font-mono text-[10px] uppercase tracking-[0.2em] text-[#777]">no invites yet.</div>
                )}
                {items.map((it) => {
                    const url = `${base}/invite/${it.token}`;
                    const isEditing = editingToken === it.token;
                    return (
                        <div key={it.token} data-testid={`invite-row-${it.token}`} className="border-b border-[#333]/10">
                            <div className="grid grid-cols-12 gap-2 px-4 py-3 items-center text-xs">
                                <div className="col-span-2 font-heading font-bold">{it.name || "—"}</div>
                                <div className="col-span-4 font-mono text-[10px] break-all">
                                    <a href={url} target="_blank" rel="noreferrer" className="hover:text-[#5D3FD3] underline">{url}</a>
                                    <button onClick={() => copy(url)} data-testid={`invite-copy-${it.token}`} className="ml-2 text-[9px] uppercase tracking-[0.2em] text-[#5D3FD3]">copy</button>
                                </div>
                                <div className="col-span-1 text-center font-mono">{(it.opens || []).length}</div>
                                <div className="col-span-2 text-center font-mono">
                                    {/* Scroll depth — updated by /invites/public/{token}/read heartbeat
                                        every 5s while the invitee scrolls. Ratchets up only. */}
                                    <span className="text-[color:var(--sc-accent)]">{it.read_percent || 0}%</span>
                                </div>
                                <div className="col-span-2 text-center">
                                    {/* Submitted — strictly yes/no. Click row-edit or expand for details. */}
                                    {it.submitted_email
                                        ? <span className="font-mono text-[10px] uppercase tracking-[0.22em] bg-[color:var(--sc-accent)] text-white px-2 py-1">yes</span>
                                        : <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#999]">no</span>}
                                </div>
                                <div className="col-span-1 text-right flex flex-col gap-1">
                                    <button onClick={() => (isEditing ? setEditingToken(null) : startEdit(it))} data-testid={`invite-edit-${it.token}`} className="text-[#5D3FD3] font-mono text-[9px] uppercase">{isEditing ? "close" : "edit"}</button>
                                    <button onClick={() => remove(it.token)} data-testid={`invite-delete-${it.token}`} className="text-[#B91C1C] font-mono text-[9px] uppercase">delete</button>
                                </div>
                            </div>
                            {isEditing && (
                                <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-3 gap-3 bg-[#FAFAFA] pt-3" data-testid={`invite-edit-form-${it.token}`}>
                                    {/* Submission details (read-only) — only when there IS one. */}
                                    {it.submitted_email && (
                                        <div className="md:col-span-3 border border-[color:var(--sc-accent)]/30 p-3 font-mono text-[10px] space-y-1">
                                            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#5D3FD3]">submission</div>
                                            {it.submitted_applicant_name && <div><b>name:</b> {it.submitted_applicant_name}</div>}
                                            <div><b>email:</b> {it.submitted_email}</div>
                                            {it.submitted_social && <div><b>social:</b> {it.submitted_social}</div>}
                                            {it.submitted_note && <div><b>note:</b> &quot;{it.submitted_note}&quot;</div>}
                                            <div className="text-[#999] text-[9px]">{(it.submitted_at || "").slice(0, 16)}</div>
                                        </div>
                                    )}
                                    <label className="block">
                                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">name</span>
                                        <input value={editDraft.name} onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))} className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                                    </label>
                                    <label className="block md:col-span-2">
                                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">note (internal)</span>
                                        <input value={editDraft.note} onChange={(e) => setEditDraft((d) => ({ ...d, note: e.target.value }))} className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none" />
                                    </label>
                                    <label className="block md:col-span-3">
                                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">personal message (shown to the invitee — supports line breaks)</span>
                                        <textarea rows={3} value={editDraft.personal_message} onChange={(e) => setEditDraft((d) => ({ ...d, personal_message: e.target.value }))} data-testid={`invite-edit-personal-message-${it.token}`} className="mt-1 w-full bg-transparent border border-[#333] font-mono text-xs p-2 focus:border-[#5D3FD3] outline-none resize-y" />
                                    </label>
                                    <div className="md:col-span-3 flex justify-end">
                                        <button onClick={saveEdit} data-testid={`invite-save-${it.token}`} className="bg-black text-white hover:bg-[#5D3FD3] transition-colors px-5 py-2 font-mono text-[10px] uppercase tracking-[0.3em]">
                                            save
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


// -----------------------------------------------------------------------------
// SUBSCRIBERS EDITOR — list newsletter signups from the home JOIN section.
// -----------------------------------------------------------------------------
function SubscribersEditor() {
    const [items, setItems] = useState([]);
    const load = async () => { try { const r = await api.get("/subscriptions"); setItems(r.data); } catch (_) { /* noop */ } };
    useEffect(() => { load(); }, []);
    const remove = async (id) => { if (!window.confirm("delete subscription?")) return; await api.delete(`/subscriptions/${id}`); load(); };
    const exportCsv = () => {
        const rows = ["email,source,created_at", ...items.map((s) => `${s.email},${s.source},${s.created_at}`)].join("\n");
        const blob = new Blob([rows], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "subscribers.csv"; a.click();
        URL.revokeObjectURL(url);
    };
    return (
        <div data-testid="admin-subscribers" className="space-y-4 max-w-4xl">
            <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">newsletter subscribers · {items.length}</div>
                <button onClick={exportCsv} data-testid="subs-export" className="border border-[#333] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] hover:bg-black hover:text-white">export csv</button>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                anyone who fills the newsletter form in the home JOIN section shows up here.
            </p>
            <div className="border border-[#333]/15">
                <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#333]/15 font-mono text-[9px] uppercase tracking-[0.25em] text-[#555]">
                    <div className="col-span-6">email</div>
                    <div className="col-span-3">source</div>
                    <div className="col-span-2">date</div>
                    <div className="col-span-1"></div>
                </div>
                {items.length === 0 && (
                    <div className="px-4 py-6 font-mono text-[10px] uppercase tracking-[0.2em] text-[#777]">no subscribers yet.</div>
                )}
                {items.map((s) => (
                    <div key={s.id} data-testid={`sub-row-${s.id}`} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#333]/10 items-center text-xs">
                        <div className="col-span-6 font-mono">{s.email}</div>
                        <div className="col-span-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#777]">{s.source}</div>
                        <div className="col-span-2 font-mono text-[10px] text-[#777]">{(s.created_at || "").slice(0, 10)}</div>
                        <div className="col-span-1 text-right">
                            <button onClick={() => remove(s.id)} data-testid={`sub-delete-${s.id}`} className="text-[#B91C1C] font-mono text-[9px] uppercase">delete</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


// -----------------------------------------------------------------------------
// MEMBERS EDITOR — list registered users, delete
// -----------------------------------------------------------------------------
function MembersEditor() {
    const [users, setUsers] = useState([]);
    const load = async () => { try { const r = await api.get("/admin/users"); setUsers(r.data); } catch (_) { /* noop */ } };
    useEffect(() => { load(); }, []);
    const remove = async (id) => { if (!window.confirm("delete user?")) return; await api.delete(`/admin/users/${id}`); load(); };
    return (
        <div data-testid="admin-members" className="space-y-4 max-w-4xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">members</div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555] leading-relaxed">
                anyone who registers via <code>/register</code> shows up here. delete to revoke access.
            </p>
            <div className="border border-[#333]/15">
                <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#333]/15 font-mono text-[9px] uppercase tracking-[0.25em] text-[#555]">
                    <div className="col-span-3">name</div>
                    <div className="col-span-4">email</div>
                    <div className="col-span-2">role</div>
                    <div className="col-span-2">registered</div>
                    <div className="col-span-1"></div>
                </div>
                {users.map((u) => (
                    <div key={u.id} data-testid={`user-row-${u.id}`} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#333]/10 items-center text-xs">
                        <div className="col-span-3 font-heading font-bold">{u.name || "—"}</div>
                        <div className="col-span-4 font-mono">{u.email}</div>
                        <div className="col-span-2 font-mono uppercase text-[10px] tracking-[0.2em]">{u.role}</div>
                        <div className="col-span-2 font-mono text-[10px] text-[#777]">{(u.created_at || "").slice(0, 16)}</div>
                        <div className="col-span-1 text-right">
                            {u.role !== "admin" && (
                                <button onClick={() => remove(u.id)} data-testid={`user-delete-${u.id}`} className="text-[#B91C1C] font-mono text-[9px] uppercase">delete</button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


// -----------------------------------------------------------------------------
// PROJECTS EDITOR — CRUD member_projects. Fully linked to members via member_ids.
// -----------------------------------------------------------------------------
function ProjectsEditor() {
    const [items, setItems] = useState([]);
    const [users, setUsers] = useState([]);
    const [editing, setEditing] = useState(null); // project object or null
    const load = async () => {
        try {
            const [p, u] = await Promise.all([api.get("/admin/projects"), api.get("/admin/users")]);
            setItems(p.data); setUsers(u.data);
        } catch (_) { /* noop */ }
    };
    useEffect(() => { load(); }, []);
    const empty = () => ({ title: "", period_start: "", period_end: "", sales: 0, costs: 0, net_profit: 0, collab_share: 0, collective_share: 0, collaborators: [], member_ids: [] });
    const save = async () => {
        const body = { ...editing };
        ["sales","costs","net_profit","collab_share","collective_share"].forEach((k) => { body[k] = parseFloat(body[k]) || 0; });
        try {
            if (editing.id) await api.put(`/admin/projects/${editing.id}`, body);
            else await api.post("/admin/projects", body);
            toast.success("saved"); setEditing(null); load();
        } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "error"); }
    };
    const remove = async (id) => { if (!window.confirm("delete?")) return; await api.delete(`/admin/projects/${id}`); load(); };
    return (
        <div data-testid="admin-projects" className="space-y-4 max-w-4xl">
            <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">projects</div>
                <button onClick={() => setEditing(empty())} data-testid="project-new" className="bg-black text-white hover:bg-[#5D3FD3] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em]">+ new</button>
            </div>
            {items.map((p) => (
                <div key={p.id} data-testid={`project-row-${p.id}`} className="border border-[#333]/15 p-4 flex items-center justify-between">
                    <div>
                        <div className="font-heading font-bold text-sm uppercase">{p.title}</div>
                        <div className="font-mono text-[10px] text-[#777] mt-1">{p.period_start} → {p.period_end} · net ${p.net_profit} · members {(p.member_ids || []).length}</div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => setEditing(p)} className="border border-[#333] px-3 py-1 font-mono text-[10px] uppercase">edit</button>
                        <button onClick={() => remove(p.id)} className="text-[#B91C1C] font-mono text-[10px] uppercase">delete</button>
                    </div>
                </div>
            ))}
            {editing && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-6 overflow-auto">
                    <div className="bg-white max-w-2xl w-full p-6 space-y-3">
                        <div className="flex justify-between items-center">
                            <div className="font-heading font-black uppercase text-lg">{editing.id ? "edit" : "new"} project</div>
                            <button onClick={() => setEditing(null)} className="font-mono text-xs">×</button>
                        </div>
                        <input value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value})} placeholder="title" className="w-full border border-[#333] p-2 font-mono text-sm" />
                        <div className="grid grid-cols-2 gap-2">
                            <input value={editing.period_start} onChange={(e) => setEditing({...editing, period_start: e.target.value})} placeholder="period start (YYYY-MM-DD)" className="border border-[#333] p-2 font-mono text-xs" />
                            <input value={editing.period_end} onChange={(e) => setEditing({...editing, period_end: e.target.value})} placeholder="period end (YYYY-MM-DD)" className="border border-[#333] p-2 font-mono text-xs" />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#555]">sales
                                <input type="number" value={editing.sales} onChange={(e) => setEditing({...editing, sales: e.target.value})} className="w-full border border-[#333] p-2 font-mono text-xs mt-1" />
                            </label>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#555]">costs
                                <input type="number" value={editing.costs} onChange={(e) => setEditing({...editing, costs: e.target.value})} className="w-full border border-[#333] p-2 font-mono text-xs mt-1" />
                            </label>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#555]">net profit
                                <input type="number" value={editing.net_profit} onChange={(e) => setEditing({...editing, net_profit: e.target.value})} className="w-full border border-[#333] p-2 font-mono text-xs mt-1" />
                            </label>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#555]">collab share
                                <input type="number" value={editing.collab_share} onChange={(e) => setEditing({...editing, collab_share: e.target.value})} className="w-full border border-[#333] p-2 font-mono text-xs mt-1" />
                            </label>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#555]">collective share
                                <input type="number" value={editing.collective_share} onChange={(e) => setEditing({...editing, collective_share: e.target.value})} className="w-full border border-[#333] p-2 font-mono text-xs mt-1" />
                            </label>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[#555] mb-1">linked members</div>
                            <div className="flex flex-wrap gap-2">
                                {users.filter((u) => u.role === "member").map((u) => {
                                    const on = (editing.member_ids || []).includes(u.id);
                                    return (
                                        <button key={u.id} type="button" onClick={() => setEditing({...editing, member_ids: on ? editing.member_ids.filter((x) => x !== u.id) : [...(editing.member_ids || []), u.id]})} className={`px-3 py-1 border font-mono text-[10px] uppercase ${on ? "bg-black text-white border-black" : "border-[#333]"}`}>
                                            {u.name || u.email}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <button onClick={save} data-testid="project-save" className="bg-black text-white hover:bg-[#5D3FD3] px-6 py-3 font-mono text-xs uppercase tracking-[0.3em] w-full">save</button>
                    </div>
                </div>
            )}
        </div>
    );
}


// -----------------------------------------------------------------------------
// FINANCE EDITOR — per-user rows (bi-weekly payouts + project earnings).
// -----------------------------------------------------------------------------
function FinanceEditor() {
    const [users, setUsers] = useState([]);
    const [finances, setFinances] = useState({}); // user_id → finance doc
    const [expanded, setExpanded] = useState(null);
    const load = async () => {
        try {
            const [u, f] = await Promise.all([api.get("/admin/users"), api.get("/admin/finance")]);
            setUsers(u.data);
            const map = {};
            (f.data || []).forEach((x) => { map[x.user_id] = x; });
            setFinances(map);
        } catch (_) { /* noop */ }
    };
    useEffect(() => { load(); }, []);
    const save = async (uid, doc) => {
        try {
            await api.put(`/admin/finance/${uid}`, { user_id: uid, totals: doc.totals || {}, rows: doc.rows || [] });
            toast.success("finance saved");
            load();
        } catch { toast.error("error"); }
    };
    return (
        <div data-testid="admin-finance" className="space-y-3 max-w-5xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">finance per member</div>
            {users.filter((u) => u.role === "member").map((u) => {
                const doc = finances[u.id] || { user_id: u.id, totals: {}, rows: [] };
                const isOpen = expanded === u.id;
                return (
                    <div key={u.id} className="border border-[#333]/15">
                        <button onClick={() => setExpanded(isOpen ? null : u.id)} className="w-full px-4 py-3 flex items-center justify-between text-left">
                            <div>
                                <div className="font-heading font-bold uppercase text-sm">{u.name || u.email}</div>
                                <div className="font-mono text-[10px] text-[#777]">{u.email} · {(doc.rows || []).length} rows</div>
                            </div>
                            <span className="font-mono text-[10px] uppercase">{isOpen ? "close" : "edit"}</span>
                        </button>
                        {isOpen && (
                            <FinanceRowsEditor uid={u.id} doc={doc} onSave={(d) => save(u.id, d)} />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function FinanceRowsEditor({ uid, doc, onSave }) {
    const [rows, setRows] = useState(doc.rows || []);
    const [totals, setTotals] = useState(doc.totals || {});
    const addRow = () => setRows([...rows, { date: "", project: "", type: "flat", gross: 0, status: "scheduled" }]);
    const updateRow = (i, k, v) => { const next = [...rows]; next[i] = { ...next[i], [k]: v }; setRows(next); };
    const removeRow = (i) => setRows(rows.filter((_, j) => j !== i));
    return (
        <div className="p-4 space-y-2 bg-[#FAFAFA]" data-testid={`finance-editor-${uid}`}>
            <div className="grid grid-cols-3 gap-2">
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#555]">lifetime gross
                    <input type="number" value={totals.lifetime_gross ?? ""} onChange={(e) => setTotals({...totals, lifetime_gross: parseFloat(e.target.value) || 0})} className="w-full border border-[#333] p-1.5 font-mono text-xs mt-1" />
                </label>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#555]">next payout date
                    <input value={totals.next_payout_date || ""} onChange={(e) => setTotals({...totals, next_payout_date: e.target.value})} placeholder="YYYY-MM-DD" className="w-full border border-[#333] p-1.5 font-mono text-xs mt-1" />
                </label>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#555]">next payout estimate
                    <input type="number" value={totals.next_payout_estimate ?? ""} onChange={(e) => setTotals({...totals, next_payout_estimate: parseFloat(e.target.value) || 0})} className="w-full border border-[#333] p-1.5 font-mono text-xs mt-1" />
                </label>
            </div>
            <div className="pt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">rows</div>
            {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-1 items-center">
                    <input value={r.date} onChange={(e) => updateRow(i, "date", e.target.value)} placeholder="YYYY-MM-DD" className="col-span-2 border border-[#333] p-1.5 font-mono text-[10px]" />
                    <input value={r.project} onChange={(e) => updateRow(i, "project", e.target.value)} placeholder="project / label" className="col-span-4 border border-[#333] p-1.5 font-mono text-[10px]" />
                    <select value={r.type} onChange={(e) => updateRow(i, "type", e.target.value)} className="col-span-2 border border-[#333] p-1.5 font-mono text-[10px]">
                        <option value="flat">flat</option>
                        <option value="revshare">revshare</option>
                        <option value="collective">collective</option>
                    </select>
                    <input type="number" value={r.gross} onChange={(e) => updateRow(i, "gross", parseFloat(e.target.value) || 0)} placeholder="$" className="col-span-2 border border-[#333] p-1.5 font-mono text-[10px]" />
                    <select value={r.status} onChange={(e) => updateRow(i, "status", e.target.value)} className="col-span-1 border border-[#333] p-1.5 font-mono text-[10px]">
                        <option value="paid">paid</option>
                        <option value="scheduled">scheduled</option>
                    </select>
                    <button type="button" onClick={() => removeRow(i)} className="col-span-1 text-[#B91C1C] font-mono text-[9px]">×</button>
                </div>
            ))}
            <div className="flex gap-2 pt-2">
                <button onClick={addRow} className="border border-[#333] px-4 py-1.5 font-mono text-[10px] uppercase">+ row</button>
                <button onClick={() => onSave({ totals, rows })} data-testid={`finance-save-${uid}`} className="bg-black text-white hover:bg-[#5D3FD3] px-4 py-1.5 font-mono text-[10px] uppercase">save</button>
            </div>
        </div>
    );
}

// ---------- SCENE FEATURE EDITOR ----------
// Modal form for creating/editing a curated editorial block (SceneFeature).
// Every field is optional except title — the frontend gracefully degrades.
const SCENE_KINDS = [
    "release", "artist", "visual", "sticker", "apparel", "digital",
    "event", "party", "collab", "photo", "video", "editorial",
    "collection", "playlist", "exhibition",
];
const SCENE_SCOPES = ["home", "artists", "events", "collections", "global"];

function SceneEditor({ data, setData, artists = [] }) {
    const [importList, setImportList] = useState({ events: [], products: [], collabs: [] });
    const related = Array.isArray(data.related_items) ? data.related_items : [];
    const addRelated = () => setData({ related_items: [...related, { title: "", subtitle: "", image_url: "", url: "", kind: "" }] });
    const updateRelated = (i, patch) => {
        const next = related.map((r, j) => (j === i ? { ...r, ...patch } : r));
        setData({ related_items: next });
    };
    const removeRelated = (i) => setData({ related_items: related.filter((_, j) => j !== i) });

    useEffect(() => {
        Promise.all([
            api.get("/events").catch(() => ({ data: [] })),
            api.get("/products").catch(() => ({ data: [] })),
            api.get("/collaborations").catch(() => ({ data: [] })),
        ]).then(([e, p, c]) => setImportList({
            events: Array.isArray(e.data) ? e.data : [],
            products: Array.isArray(p.data) ? p.data : [],
            collabs: Array.isArray(c.data) ? c.data : [],
        }));
    }, []);

    // Import prefill — pulls fields from an existing artist/event/product/collab
    // into the scene feature so it doesn't have to be typed by hand.
    const importFrom = (kind, id) => {
        if (!id) return;
        if (kind === "artist") {
            const a = artists.find((x) => x.id === id || x.slug === id);
            if (!a) return;
            setData({
                kind: "artist",
                title: a.name,
                subtitle: (a.disciplines || [a.discipline]).filter(Boolean).slice(0, 2).join(" · "),
                hero_image_url: a.image_url || a.image_url_alt || "",
                short_description: a.short_description || a.bio || "",
                credit_label: a.name,
                credit_url: `/artists/${a.slug}`,
                collaborator_slugs: [a.slug],
                embed_url: (a.embeds || [])[0] || "",
                source_kind: "artist",
                source_id: a.slug || a.id,
            });
        }
        if (kind === "event") {
            const e = importList.events.find((x) => x.id === id);
            if (!e) return;
            setData({
                kind: "event",
                title: e.title,
                subtitle: [e.venue, e.city].filter(Boolean).join(" · "),
                hero_image_url: e.image_url || e.flyer_url || "",
                short_description: e.description || "",
                cta_label: e.ticket_url ? "tickets" : "",
                cta_url: e.ticket_url || e.venue_url || "",
                source_kind: "event",
                source_id: e.id,
            });
        }
        if (kind === "product") {
            const p = importList.products.find((x) => x.id === id);
            if (!p) return;
            setData({
                kind: "apparel",
                title: p.title,
                subtitle: p.artist_credit || "",
                hero_image_url: p.image_url || "",
                short_description: p.description || "",
                credit_label: p.artist_credit || "",
                credit_url: p.artist_slug ? `/artists/${p.artist_slug}` : "",
                cta_label: p.etsy_url ? "shop on etsy" : "shop",
                cta_url: p.etsy_url || p.shopify_url || "",
                source_kind: "product",
                source_id: p.id,
            });
        }
        if (kind === "collab") {
            const c = importList.collabs.find((x) => x.id === id);
            if (!c) return;
            setData({
                kind: "collab",
                title: c.title,
                subtitle: c.partner || "",
                hero_image_url: c.image_url || "",
                short_description: c.description || "",
                cta_label: "learn more",
                cta_url: c.url || "",
                source_kind: "collab",
                source_id: c.id,
            });
        }
    };

    return (
        <>
            {/* Import prefill */}
            <div className="p-3 bg-[#F5F0E8] border-l-2 border-[#5D3FD3] mb-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-2">
                    import from existing content (recommended)
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <select
                        onChange={(e) => { importFrom("artist", e.target.value); e.target.value = ""; }}
                        defaultValue=""
                        data-testid="scene-import-artist"
                        className="bg-white border border-[#333] font-mono text-[11px] p-1.5"
                    >
                        <option value="">import artist…</option>
                        {artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                    <select
                        onChange={(e) => { importFrom("event", e.target.value); e.target.value = ""; }}
                        defaultValue=""
                        data-testid="scene-import-event"
                        className="bg-white border border-[#333] font-mono text-[11px] p-1.5"
                    >
                        <option value="">import event…</option>
                        {importList.events.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
                    </select>
                    <select
                        onChange={(e) => { importFrom("product", e.target.value); e.target.value = ""; }}
                        defaultValue=""
                        data-testid="scene-import-product"
                        className="bg-white border border-[#333] font-mono text-[11px] p-1.5"
                    >
                        <option value="">import product…</option>
                        {importList.products.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
                    </select>
                    <select
                        onChange={(e) => { importFrom("collab", e.target.value); e.target.value = ""; }}
                        defaultValue=""
                        data-testid="scene-import-collab"
                        className="bg-white border border-[#333] font-mono text-[11px] p-1.5"
                    >
                        <option value="">import collab…</option>
                        {importList.collabs.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
                    </select>
                </div>
                {data.source_kind && (
                    <div className="mt-2 font-mono text-[10px] text-[#555]">
                        imported from <b>{data.source_kind}</b> · {data.source_id}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">kind</span>
                    <select
                        value={data.kind || "editorial"}
                        onChange={(e) => setData({ kind: e.target.value })}
                        data-testid="scene-kind"
                        className="mt-1 w-full bg-transparent border border-[#333] font-mono text-sm p-2 focus:border-[#5D3FD3] outline-none"
                    >
                        {SCENE_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">show in — scene links out · radio has players</span>
                    <select
                        value={data.channel || "scene"}
                        onChange={(e) => setData({ channel: e.target.value })}
                        data-testid="scene-channel"
                        className="mt-1 w-full bg-transparent border border-[#333] font-mono text-sm p-2 focus:border-[#5D3FD3] outline-none"
                    >
                        <option value="scene">the scene (no player)</option>
                        <option value="radio">itzkatu radio (with player)</option>
                        <option value="both">both sections</option>
                    </select>
                </label>
                <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">click action — what clicking the image does</span>
                    <select
                        value={data.click_action || ""}
                        onChange={(e) => setData({ click_action: e.target.value })}
                        data-testid="scene-click-action"
                        className="mt-1 w-full bg-transparent border border-[#333] font-mono text-sm p-2 focus:border-[#5D3FD3] outline-none"
                    >
                        <option value="">auto (radio → play · scene → open link)</option>
                        <option value="play">play the media (sticky player)</option>
                        <option value="link">open the link</option>
                    </select>
                </label>
                <SchemePicker
                    scheme={data.scheme}
                    onChange={(v) => setData({ scheme: v })}
                    label="scheme — drives the glitch effect palette AND the hover tint when no alt image is set"
                    testId="scene-scheme"
                />
            </div>
            <Input label="title (usually inherited from import — editable)" value={data.title || ""} onChange={(e) => setData({ title: e.target.value })} data-testid="scene-title" className="text-lg" />
            <Input label="custom label (e.g. apparel · music video · live installation)" value={data.subtitle || ""} onChange={(e) => setData({ subtitle: e.target.value })} data-testid="scene-custom-label" className="text-lg" />
            <RenderPresetPicker
                preset={data.render_preset}
                assetUrl={data.render_asset_url}
                onChange={(patch) => setData(patch)}
            />
            {/* Shape — how the tile is displayed in the media player grid. */}
            <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-2">
                    tile shape
                </div>
                <div className="grid grid-cols-4 gap-2">
                    {[
                        { v: "", label: "auto", box: "aspect-square" },
                        { v: "square", label: "square", box: "aspect-square" },
                        { v: "tall", label: "tall", box: "aspect-[3/4]" },
                        { v: "wide", label: "wide", box: "aspect-[16/9]" },
                    ].map((opt) => {
                        const active = (data.aspect || "") === opt.v;
                        return (
                            <button
                                key={opt.v || "auto"}
                                type="button"
                                onClick={() => setData({ aspect: opt.v })}
                                data-testid={`scene-aspect-${opt.v || "auto"}`}
                                className={`flex flex-col items-center gap-2 border px-2 py-2 transition ${active ? "border-[#5D3FD3] bg-[#F5F0FF]" : "border-[#333] hover:bg-[#F5F5F5]"}`}
                            >
                                <div className={`${opt.box} w-10 bg-[#333]`} />
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em]">
                                    {opt.label}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
            <details className="border-t border-[#DDD] pt-4 mt-2">
                <summary className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] cursor-pointer">
                    advanced (image override, cta, embed, related)
                </summary>
                <div className="pt-3 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <ImageInput label="hero image url (override the imported image)" value={data.hero_image_url || ""} onChange={(e) => setData({ hero_image_url: e.target.value })} />
                        <Input label="hero video url (optional mp4)" value={data.hero_video_url || ""} onChange={(e) => setData({ hero_video_url: e.target.value })} />
                    </div>
                    <ImageInput label="poster image (fallback for video)" value={data.poster_image_url || ""} onChange={(e) => setData({ poster_image_url: e.target.value })} />
                    <Textarea rows={3} label="short description (only if you need an extra editorial line)" value={data.short_description || ""} onChange={(e) => setData({ short_description: e.target.value })} data-testid="scene-desc" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Input label="credit label (override)" value={data.credit_label || ""} onChange={(e) => setData({ credit_label: e.target.value })} />
                        <Input label="credit url (override)" value={data.credit_url || ""} onChange={(e) => setData({ credit_url: e.target.value })} />
                    </div>
                    <label className="block">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">collaborators — artist slugs</span>
                        <input
                            value={Array.isArray(data.collaborator_slugs) ? data.collaborator_slugs.join(", ") : (data.collaborator_slugs || "")}
                            onChange={(e) => setData({ collaborator_slugs: e.target.value })}
                            placeholder="quantaloop, deuteroz"
                            className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                        />
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Input label="cta label" value={data.cta_label || ""} onChange={(e) => setData({ cta_label: e.target.value })} />
                        <Input label="cta url" value={data.cta_url || ""} onChange={(e) => setData({ cta_url: e.target.value })} />
                    </div>
                    <Input label="embed url (spotify / sc / yt / ig)" value={data.embed_url || ""} onChange={(e) => setData({ embed_url: e.target.value })} />
                </div>
            </details>
            {/* Related items — reusable mini-cards */}
            <div className="border-t border-[#DDD] pt-4">
                <div className="flex items-baseline justify-between mb-2">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">
                        related items (up to 4 — cards shown under this feature)
                    </div>
                    <button type="button" onClick={addRelated} className="font-mono text-[10px] uppercase tracking-[0.2em] border border-[#333] px-2 py-1 hover:bg-[#333] hover:text-white">
                        + related
                    </button>
                </div>
                {related.map((r, i) => (
                    <div key={i} className="border border-[#DDD] p-3 mb-2 space-y-2">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                            <input value={r.title || ""} onChange={(e) => updateRelated(i, { title: e.target.value })} placeholder="title" className="border border-[#333] p-1.5 font-mono text-xs" />
                            <input value={r.subtitle || ""} onChange={(e) => updateRelated(i, { subtitle: e.target.value })} placeholder="subtitle" className="border border-[#333] p-1.5 font-mono text-xs" />
                            <select value={r.kind || ""} onChange={(e) => updateRelated(i, { kind: e.target.value })} className="border border-[#333] p-1.5 font-mono text-xs">
                                <option value="">(kind)</option>
                                {SCENE_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                            </select>
                            <button type="button" onClick={() => removeRelated(i)} className="font-mono text-xs uppercase text-[#B91C1C]">remove</button>
                        </div>
                        <input value={r.image_url || ""} onChange={(e) => updateRelated(i, { image_url: e.target.value })} placeholder="image url" className="w-full border border-[#333] p-1.5 font-mono text-xs" />
                        <input value={r.url || ""} onChange={(e) => updateRelated(i, { url: e.target.value })} placeholder="link (/shop, /artists/x, https://…)" className="w-full border border-[#333] p-1.5 font-mono text-xs" />
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="order (lower = shown first)" type="number" value={data.order || 0} onChange={(e) => setData({ order: parseInt(e.target.value, 10) || 0 })} />
                <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] pt-6">
                    <input type="checkbox" checked={data.active !== false} onChange={(e) => setData({ active: e.target.checked })} />
                    active (visible on the site)
                </label>
            </div>
            <RenderPresetPicker
                preset={data.render_preset}
                assetUrl={data.render_asset_url}
                onChange={(patch) => setData(patch)}
            />
        </>
    );
}

// -----------------------------------------------------------------------------
// RENDER PRESET PICKER — experimental visual identity per item.
// Presets: original / pixel / dither / ascii / crt / rgb-glitch / monochrome / loop.
// -----------------------------------------------------------------------------
const RENDER_PRESET_OPTIONS = [
    { value: "", label: "random (rotates through presets)" },
    { value: "original", label: "original (photo, no effect)" },
    { value: "pixel", label: "pixel display" },
    { value: "pixel-h-lines", label: "pixel — horizontal lines" },
    { value: "pixel-v-lines", label: "pixel — vertical lines" },
    { value: "pixel-cross", label: "pixel — cross bands" },
    { value: "dither", label: "dithered / noise" },
    { value: "ascii", label: "ASCII render" },
    { value: "crt", label: "CRT / scanlines" },
    { value: "loop", label: "animated loop (upload url)" },
];

function RenderPresetPicker({ preset = "", assetUrl = "", onChange }) {
    return (
        <div className="border-t border-[#DDD] pt-4 mt-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-2">
                visual identity (homepage tile)
            </div>
            <p className="font-mono text-[10px] text-[#777] leading-relaxed mb-3">
                choose how this project appears on the homepage. the original image
                always remains on the dedicated page. leave on <b>auto</b> to let itzkatu
                pick a treatment based on the item — this is what makes the gallery feel experimental.
            </p>
            <label className="block mb-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">render preset</span>
                <select
                    value={preset || ""}
                    onChange={(e) => onChange({ render_preset: e.target.value })}
                    data-testid="render-preset-select"
                    className="mt-1 w-full bg-transparent border border-[#333] font-mono text-sm p-2 focus:border-[#5D3FD3] outline-none"
                >
                    {RENDER_PRESET_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </label>
            {preset === "loop" && (
                <Input
                    label="loop asset url (mp4 / webm / gif)"
                    value={assetUrl || ""}
                    onChange={(e) => onChange({ render_asset_url: e.target.value })}
                    placeholder="https://.../release-loop.mp4"
                />
            )}
        </div>
    );
}


const AFTER_DARK_KINDS = ["audio", "video", "reel", "photo", "wallpaper", "zine", "download", "story", "embed", "link"];

function AfterDarkEditor({ data, setData, artists = [] }) {
    const kind = data.kind || "audio";
    return (
        <>
            <div className="p-3 bg-[#0B0B0C] text-white mb-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F0C56C]">
                    después de la caída · pieza del canal oculto
                </div>
                <div className="font-mono text-[10px] text-white/60 mt-1">
                    visible en <b>/after-dark</b> (el QR de los pedidos).
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">kind</span>
                    <select
                        value={kind}
                        onChange={(e) => setData({ kind: e.target.value })}
                        className="mt-1 w-full bg-transparent border border-[#333] font-mono text-sm p-2 focus:border-[#5D3FD3] outline-none"
                        data-testid="after-dark-kind"
                    >
                        {AFTER_DARK_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">artist slug (optional — links to their page)</span>
                    <input
                        value={data.artist_slug || ""}
                        list="after-dark-artist-slugs"
                        onChange={(e) => setData({ artist_slug: e.target.value })}
                        placeholder="quantaloop"
                        className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                    />
                    <datalist id="after-dark-artist-slugs">
                        {artists.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
                    </datalist>
                </label>
            </div>
            <Input label="title (fallback)" value={data.title || ""} onChange={(e) => setData({ title: e.target.value })} data-testid="after-dark-title" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="title · EN" value={data.title_en || ""} onChange={(e) => setData({ title_en: e.target.value })} data-testid="after-dark-title-en" />
                <Input label="title · ES" value={data.title_es || ""} onChange={(e) => setData({ title_es: e.target.value })} data-testid="after-dark-title-es" />
            </div>
            <Input label="subtitle (fallback — small line under title)" value={data.subtitle || ""} onChange={(e) => setData({ subtitle: e.target.value })} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="subtitle · EN" value={data.subtitle_en || ""} onChange={(e) => setData({ subtitle_en: e.target.value })} data-testid="after-dark-subtitle-en" />
                <Input label="subtitle · ES" value={data.subtitle_es || ""} onChange={(e) => setData({ subtitle_es: e.target.value })} data-testid="after-dark-subtitle-es" />
            </div>
            <Textarea rows={3} label="description (fallback — editorial paragraph)" value={data.description || ""} onChange={(e) => setData({ description: e.target.value })} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Textarea rows={3} label="description · EN" value={data.description_en || ""} onChange={(e) => setData({ description_en: e.target.value })} data-testid="after-dark-description-en" />
                <Textarea rows={3} label="description · ES" value={data.description_es || ""} onChange={(e) => setData({ description_es: e.target.value })} data-testid="after-dark-description-es" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                    label={kind === "audio" || kind === "video" || kind === "reel" || kind === "embed" ? "embed url (Spotify / SC / YT / IG / Bandcamp)" : "embed url (optional)"}
                    value={data.embed_url || ""}
                    onChange={(e) => setData({ embed_url: e.target.value })}
                />
                <ImageInput label="image / thumbnail url" value={data.image_url || ""} onChange={(e) => setData({ image_url: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                    label={kind === "wallpaper" || kind === "zine" || kind === "download" ? "download url — direct file (jpg/pdf/mp3/zip)" : "download url (optional)"}
                    value={data.media_url || ""}
                    onChange={(e) => setData({ media_url: e.target.value })}
                />
                <Input label="external url (outbound link — opens in new tab)" value={data.external_url || ""} onChange={(e) => setData({ external_url: e.target.value })} />
            </div>
            <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">tags (comma-separated)</span>
                <input
                    value={Array.isArray(data.tags) ? data.tags.join(", ") : (data.tags || "")}
                    onChange={(e) => setData({ tags: e.target.value })}
                    placeholder="mix, warm-up, quantaloop"
                    className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                />
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">publish at (ISO — hide until this moment · empty = live now)</span>
                    <input
                        type="datetime-local"
                        value={(data.publish_at || "").slice(0, 16)}
                        onChange={(e) => setData({ publish_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                        className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                    />
                </label>
                <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">expires at (hide after · empty = forever)</span>
                    <input
                        type="datetime-local"
                        value={(data.expires_at || "").slice(0, 16)}
                        onChange={(e) => setData({ expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                        className="mt-1 w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-2"
                    />
                </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input label="order (lower = first)" type="number" value={data.order || 0} onChange={(e) => setData({ order: parseInt(e.target.value, 10) || 0 })} />
                <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] pt-6">
                    <input type="checkbox" checked={data.active !== false} onChange={(e) => setData({ active: e.target.checked })} />
                    active
                </label>
                <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] pt-6">
                    <input type="checkbox" checked={!!data.featured} onChange={(e) => setData({ featured: e.target.checked })} />
                    featured (top block)
                </label>
            </div>
        </>
    );
}


// -----------------------------------------------------------------------------
// SCHEME PICKER — reused for per-artist, per-scene, per-page overrides.
// Renders visible color SWATCHES so admins know exactly what each scheme looks like.
// -----------------------------------------------------------------------------
const SCHEME_SWATCHES = {
    "":         { bg: "transparent", fg: "#111", border: "#CCC", label: "inherit" },
    light:      { bg: "#FFFFFF", fg: "#5D3FD3", border: "#DDD" },
    mustard:    { bg: "#F5D547", fg: "#5D3FD3" },
    smoke:      { bg: "#EDEAE5", fg: "#D9376E" },
    stone:      { bg: "#E5E7E9", fg: "#E85E2C" },
    charcoal:   { bg: "#1F1F22", fg: "#F5D547" },
    night:      { bg: "#0E0E0E", fg: "#7C5CFF" },
};

const SCHEME_ORDER = ["", "light", "mustard", "smoke", "stone", "charcoal", "night"];

function SchemePicker({ scheme = "", onChange, label = "color scheme", testId = "scheme-picker" }) {
    return (
        <div className="border-t border-[#DDD] pt-4 mt-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-2">{label}</div>
            <div className="flex flex-wrap gap-2" data-testid={testId}>
                {SCHEME_ORDER.map((key) => {
                    const s = SCHEME_SWATCHES[key];
                    const active = scheme === key;
                    return (
                        <button
                            key={key || "inherit"}
                            type="button"
                            onClick={() => onChange(key)}
                            data-testid={`${testId}-${key || "inherit"}`}
                            className={`relative flex items-center gap-2 pl-1 pr-3 py-1 border-2 transition-colors ${active ? "border-[#5D3FD3]" : "border-[#DDD] hover:border-[#333]"}`}
                            title={key || "inherit page scheme"}
                        >
                            <span
                                className="inline-block w-6 h-6"
                                style={{ backgroundColor: s.bg, color: s.fg, border: s.border ? `1px solid ${s.border}` : undefined }}
                            >
                                {key === "" && <span className="block w-full h-full" style={{ backgroundImage: "linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%)", backgroundSize: "8px 8px", backgroundPosition: "0 0, 4px 4px" }} />}
                                {key !== "" && <span className="block w-full h-full" style={{ backgroundColor: s.fg, transform: "scale(0.35)", transformOrigin: "top left" }} />}
                            </span>
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em]">{key || "inherit"}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// Backwards-compat — legacy consumers still expect SCHEME_OPTIONS for <option> lists.
const SCHEME_OPTIONS = SCHEME_ORDER.map((v) => ({
    value: v,
    label: v === "" ? "inherit page scheme" : v,
}));
